import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote, 
  Package, DollarSign, Percent, X, Check, UserCheck,
  History, Printer, Clock, User, BarChart3, ChevronsUpDown, Building2, AlertTriangle, WifiOff, Settings, LayoutGrid, Camera, Coins, RotateCcw, Save, Tag, CheckCircle, Loader2, ArrowRight, Eye, Receipt
} from 'lucide-react';
import axios from 'axios';
import { formatUSD, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import ReceiptSettingsDialog from '../components/shared/ReceiptSettingsDialog';
import QuickAddAccountDialog from '../components/dialogs/QuickAddAccountDialog';
import QuickAddInventoryDialog from '../components/dialogs/QuickAddInventoryDialog';
import AIMedicineInfoDialog from '../components/dialogs/AIMedicineInfoDialog';
import PrescriptionLabelDialog from '../components/PrescriptionLabelDialog';
import DrugInteractionDialog from '../components/dialogs/DrugInteractionDialog';
import BarcodeScannerDialog from '../components/BarcodeScannerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick Item Button Component
const QuickItemButton = ({ item, onClick, showImage = true }) => {
  // Check for either S3 URL (image_url) or local file (image_filename)
  const hasImage = item.image_url || item.image_filename;
  const shouldShowImage = showImage && item.show_image_in_pos !== false && hasImage;
  
  // Get image URL - prioritize S3 URL, fall back to local
  const getImageSrc = () => {
    if (item.image_url) {
      // S3 URL - use directly (it's a full URL)
      return item.image_url;
    }
    if (item.image_filename) {
      // Local file - construct API URL
      return `${API}/inventory/image/${encodeURIComponent(item.image_filename)}`;
    }
    return null;
  };
  
  return (
    <button
      onClick={() => onClick(item)}
      className="p-2 sm:p-2 bg-card border border-border rounded-xl hover:bg-muted hover:border-primary/50 hover:shadow-lg transition-all duration-200 text-left flex flex-col group active:scale-95"
    >
      {shouldShowImage ? (
        <div className="w-full aspect-square mb-2 rounded-lg overflow-hidden bg-gradient-to-br from-muted/80 to-muted flex items-center justify-center border border-border/50">
          <img 
            src={getImageSrc()} 
            alt={item.name} 
            className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-200"
            onError={(e) => { 
              e.target.style.display = 'none'; 
              e.target.parentElement.innerHTML = '<div class="text-3xl opacity-30">📦</div>';
            }}
          />
        </div>
      ) : (
        <div className="w-full aspect-square mb-2 rounded-lg overflow-hidden bg-gradient-to-br from-muted/80 to-muted flex items-center justify-center border border-border/50">
          <span className="text-3xl opacity-30">📦</span>
        </div>
      )}
      <div className="text-xs font-mono text-cyan-400 truncate w-full">{item.barcode || item.sku || 'N/A'}</div>
      <div className="text-sm font-semibold truncate w-full leading-tight mt-0.5">{item.name}</div>
      <div className="flex justify-between items-center w-full mt-1">
        <span className="text-sm font-bold text-primary">{item.currency || 'XOF'} {formatUSD(item.price)}</span>
        <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{item.on_hand_qty || 0}</span>
      </div>
    </button>
  );
};

// Searchable Item Selector for POS
const POSItemSelector = ({ items, onSelect, organizationId }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [aiItem, setAiItem] = useState(null);

  const filteredItems = useMemo(() => {
    if (!search) return items.slice(0, 20);
    const searchLower = search.toLowerCase();
    const searchTrimmed = search.trim();
    
    // Check for exact item_code match first (for Enter key functionality)
    const exactCodeMatch = items.find(item => 
      item.item_code === searchTrimmed || item.sku === searchTrimmed
    );
    
    // Filter by item_code, barcode, name, or Arabic name
    const filtered = items.filter(item => 
      (item.item_code && item.item_code.toLowerCase().includes(searchLower)) ||
      (item.sku && item.sku.toLowerCase().includes(searchLower)) ||
      (item.barcode && item.barcode.toLowerCase().includes(searchLower)) ||
      item.name.toLowerCase().includes(searchLower) ||
      (item.name_ar && item.name_ar.includes(search))
    );
    
    // If there's an exact code match, put it first
    if (exactCodeMatch) {
      const others = filtered.filter(i => i.id !== exactCodeMatch.id);
      return [exactCodeMatch, ...others].slice(0, 20);
    }
    
    return filtered.slice(0, 20);
  }, [items, search]);

  // Handle Enter key to add exact match or first result
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim()) {
      e.preventDefault();
      const searchTrimmed = search.trim();
      
      // First try exact item_code/sku match
      const exactMatch = items.find(item => 
        item.item_code === searchTrimmed || item.sku === searchTrimmed
      );
      
      if (exactMatch) {
        onSelect(exactMatch);
        setOpen(false);
        setSearch('');
        return;
      }
      
      // Then try exact barcode match
      const barcodeMatch = items.find(item => item.barcode === searchTrimmed);
      if (barcodeMatch) {
        onSelect(barcodeMatch);
        setOpen(false);
        setSearch('');
        return;
      }
      
      // Otherwise add first filtered result
      if (filteredItems.length > 0) {
        onSelect(filteredItems[0]);
        setOpen(false);
        setSearch('');
      }
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between h-12 text-lg">
          <span className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Search Items...
          </span>
          <ChevronsUpDown className="w-5 h-5 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[700px] p-0" align="start">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Enter code, barcode, or search name... (Press Enter to add)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 h-12 text-lg"
              autoFocus
              data-testid="pos-search-input"
            />
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {filteredItems.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              No items found
            </div>
          ) : (
            filteredItems.map(item => (
              <div
                key={item.id}
                className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-muted border-b border-border/50"
              >
                <div className="flex-1 min-w-0" onClick={() => { onSelect(item); setOpen(false); setSearch(''); }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(item.item_code || item.sku) && <span className="font-mono text-xs text-orange-400 shrink-0 bg-orange-500/10 px-1.5 py-0.5 rounded">#{item.item_code || item.sku}</span>}
                    {item.barcode && <span className="font-mono text-xs text-cyan-400 shrink-0">[{item.barcode}]</span>}
                    <span className="font-medium">{item.name}</span>
                    {item.name_ar && <span className="text-xs text-muted-foreground" dir="rtl">{item.name_ar}</span>}
                  </div>
                  <div className="text-sm text-muted-foreground flex gap-3 mt-0.5">
                    <span>{item.currency || 'XOF'} {formatUSD(item.price)}</span>
                    <span>Stock: {item.on_hand_qty}</span>
                    {item.category_name && <span>{item.category_name}</span>}
                    {item.supplier_name && <span className="truncate">{item.supplier_name}</span>}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setAiItem(item); }}
                  className="p-1.5 rounded hover:bg-purple-500/20 text-purple-400 hover:text-purple-300 transition-colors shrink-0 mx-1"
                  title="AI Medicine Info"
                  data-testid={`ai-info-btn-${item.id}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
                </button>
                <Plus className="w-5 h-5 text-green-400 shrink-0" onClick={() => { onSelect(item); setOpen(false); setSearch(''); }} />
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
    {aiItem && (
      <AIMedicineInfoDialog
        open={!!aiItem}
        onOpenChange={(v) => { if (!v) setAiItem(null); }}
        itemName={aiItem.name}
        itemId={aiItem.id}
        organizationId={organizationId}
      />
    )}
    </>
  );
};

// Customer Selector Component with Server-Side Search
const CustomerSelector = ({ customers, value, onChange, organizationId, apiUrl }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [serverResults, setServerResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Server-side search when many customers or search is active
  useEffect(() => {
    if (!search || search.length < 2 || !apiUrl || !organizationId) {
      setServerResults(null);
      return;
    }
    // Trigger server search if we have 200+ customers (near the initial limit)
    if (customers.length >= 200) {
      const timer = setTimeout(async () => {
        setSearching(true);
        try {
          const response = await fetch(
            `${apiUrl}/customer-accounts?organization_id=${organizationId}&search=${encodeURIComponent(search)}`,
            { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }}
          );
          const data = await response.json();
          // Filter to only 4111 codes (customers, not customer returns 4114)
          setServerResults(data.filter(a => a.code.startsWith('4111')));
        } catch {
          setServerResults(null);
        } finally {
          setSearching(false);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
    setServerResults(null);
  }, [search, apiUrl, organizationId, customers.length]);

  const filteredCustomers = useMemo(() => {
    // Use server results if available
    if (serverResults) return serverResults;
    
    if (!search) return customers;
    const searchLower = search.toLowerCase();
    return customers.filter(c => 
      c.code.toLowerCase().includes(searchLower) ||
      c.name.toLowerCase().includes(searchLower)
    );
  }, [customers, search, serverResults]);

  const selected = customers.find(c => c.id === value) || (serverResults && serverResults.find(c => c.id === value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between h-10">
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="font-mono text-cyan-400">{selected.code}</span>
              <span>{selected.name}</span>
              <span className={`text-xs px-1 rounded ${(selected.balance_usd || 0) >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {formatUSD(Math.abs(selected.balance_usd || 0))}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">Select customer...</span>
          )}
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input
            placeholder="Search customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
            autoFocus
          />
          {searching && <div className="text-xs text-muted-foreground mt-1">Searching...</div>}
        </div>
        <div className="max-h-[250px] overflow-y-auto">
          <div
            className={`flex items-center px-3 py-2 cursor-pointer hover:bg-muted ${!value ? 'bg-muted' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            <span className="text-muted-foreground">Walk-in Customer (Cash)</span>
          </div>
          {filteredCustomers.map(c => (
            <div
              key={c.id}
              className={`flex items-center px-3 py-2 cursor-pointer hover:bg-muted ${value === c.id ? 'bg-muted' : ''}`}
              onClick={() => { onChange(c.id); setOpen(false); setSearch(''); }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-cyan-400">{c.code}</span>
                  <span>{c.name}</span>
                </div>
              </div>
              <span className={`text-xs px-1.5 py-0.5 rounded ${(c.balance_usd || 0) >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {formatUSD(Math.abs(c.balance_usd || 0))}
              </span>
            </div>
          ))}
          {filteredCustomers.length === 0 && search && !searching && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No customers found</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const POSPage = () => {
  const { currentOrg, user, isVatRegistered, isCashier, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const barcodeInputRef = useRef(null);
  
  // Data
  const [inventoryItems, setInventoryItems] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  
  // Cart State
  const [cart, setCart] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxPercent, setTaxPercent] = useState(11);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [notes, setNotes] = useState('');
  
  // Batch State
  const [itemBatches, setItemBatches] = useState({}); // { itemId: [batches] }
  const [batchSelectorOpen, setBatchSelectorOpen] = useState(null); // cart index
  
  // Display Currency
  const [displayCurrency, setDisplayCurrency] = useState('XOF');
  const [lbpRate, setLbpRate] = useState(0.0015244895);
  
  // Settings
  const [creditAccountId, setCreditAccountId] = useState('');
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [mobileTab, setMobileTab] = useState('products'); // 'products' | 'cart'
  const [showInteractions, setShowInteractions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [showReceipt, setShowReceipt] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [viewTransaction, setViewTransaction] = useState(null); // For viewing transaction from URL param
  
  // Cashier History State
  const [showCashierHistory, setShowCashierHistory] = useState(false);
  const [todayTransactions, setTodayTransactions] = useState([]);
  const [viewCashierTransaction, setViewCashierTransaction] = useState(null);
  
  // History Transaction Detail View (for super admin)
  const [viewHistoryTransaction, setViewHistoryTransaction] = useState(null);
  
  // Payment State
  const [paymentMethod, setPaymentMethod] = useState('cash'); // cash, card, customer
  const [paymentCurrency, setPaymentCurrency] = useState('XOF');
  const [paymentExchangeRate, setPaymentExchangeRate] = useState(1);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedCashAccount, setSelectedCashAccount] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Transaction to delete
  const [paymentAdjustment, setPaymentAdjustment] = useState(0); // Adjustment discount/premium based on payment amount
  const [pendingAdjustment, setPendingAdjustment] = useState(null); // Pending adjustment awaiting confirmation
  const [showAdjustmentWarning, setShowAdjustmentWarning] = useState(false); // Warning dialog for large adjustments
  const ADJUSTMENT_WARNING_THRESHOLD = 0.30; // 30% threshold for warning
  const [receiptSettings, setReceiptSettings] = useState(null);
  const [showReceiptSettings, setShowReceiptSettings] = useState(false);
  const [voidConfirm, setVoidConfirm] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [showVoided, setShowVoided] = useState(false);
  
  // Dual Currency Payment State
  const [receivedUsd, setReceivedUsd] = useState('');
  const [receivedLbp, setReceivedLbp] = useState('');
  const [changeUsd, setChangeUsd] = useState(0);
  const [changeLbp, setChangeLbp] = useState(0);
  const [changeMode, setChangeMode] = useState('auto'); // 'auto', 'lbp', 'usd', 'manual'

  // Quick Add dialog state
  const [qaCustomerOpen, setQaCustomerOpen] = useState(false);
  const [qaCustomerMode, setQaCustomerMode] = useState('add');
  const [qaCustomerEdit, setQaCustomerEdit] = useState(null);
  const [qaItemOpen, setQaItemOpen] = useState(false);
  const [qaItemMode, setQaItemMode] = useState('add');
  const [qaItemEdit, setQaItemEdit] = useState(null);

  // Parked Transactions State (Hold/Resume feature)
  const [parkedTransactions, setParkedTransactions] = useState([]);
  const [showParkedList, setShowParkedList] = useState(false);

  // Prescription Label Dialog State
  const [showPrescriptionLabel, setShowPrescriptionLabel] = useState(false);

  // Controlled Substance / Prescription State
  const [showControlledDialog, setShowControlledDialog] = useState(false);
  const [pendingControlledItem, setPendingControlledItem] = useState(null);
  const [physicians, setPhysicians] = useState([]);
  const [patients, setPatients] = useState([]);
  const [controlledFormData, setControlledFormData] = useState({
    physician_id: '',
    patient_id: '',
    instructions: '',
    treatment_length_months: 1
  });
  
  // New state for prescription lookup
  const [selectedPatientPrescriptions, setSelectedPatientPrescriptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [prescriptionLookupLoading, setPrescriptionLookupLoading] = useState(false);
  const [showPrescriptionImage, setShowPrescriptionImage] = useState(null);
  
  // New state for enhanced patient search
  const [patientsWithPrescription, setPatientsWithPrescription] = useState([]);
  const [patientsWithoutPrescription, setPatientsWithoutPrescription] = useState([]);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);

  // Fetch physicians and patients for controlled substance dialog
  useEffect(() => {
    if (!currentOrg?.id) return;
    const fetchPrescriptionData = async () => {
      try {
        const [physRes, patRes] = await Promise.all([
          axios.get(`${API}/physicians?organization_id=${currentOrg.id}`),
          axios.get(`${API}/patients?organization_id=${currentOrg.id}`)
        ]);
        setPhysicians(physRes.data || []);
        setPatients(patRes.data || []);
      } catch (error) {
        console.error('Failed to fetch prescription data:', error);
      }
    };
    fetchPrescriptionData();
  }, [currentOrg?.id]);

  // Load parked transactions from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(`pos_parked_${currentOrg?.id}`);
    if (saved) {
      try {
        setParkedTransactions(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load parked transactions:', e);
      }
    }
  }, [currentOrg?.id]);

  // Save parked transactions to localStorage when changed
  useEffect(() => {
    if (currentOrg?.id) {
      localStorage.setItem(`pos_parked_${currentOrg?.id}`, JSON.stringify(parkedTransactions));
    }
  }, [parkedTransactions, currentOrg?.id]);

  // Park current transaction
  const handleParkTransaction = () => {
    if (cart.length === 0) {
      toast.error('Cannot park an empty cart');
      return;
    }
    const parked = {
      id: Date.now().toString(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      date: new Date().toLocaleDateString(),
      cart: [...cart],
      customerId: selectedCustomerId,
      customerName: customerAccounts.find(c => c.id === selectedCustomerId)?.name || 'Walk-in',
      discountPercent,
      taxPercent,
      notes,
      totals: { ...totals }
    };
    setParkedTransactions(prev => [...prev, parked]);
    
    // Clear cart
    setCart([]);
    setSelectedCustomerId('');
    setDiscountPercent(0);
    setNotes('');
    
    toast.success(`Transaction parked! ${cart.length} item(s) saved.`);
  };

  // Resume a parked transaction
  const handleResumeTransaction = (parked) => {
    // If current cart has items, ask to park or discard
    if (cart.length > 0) {
      if (!window.confirm('Current cart has items. Discard them and resume parked transaction?')) {
        return;
      }
    }
    
    // Restore cart state
    setCart(parked.cart);
    setSelectedCustomerId(parked.customerId || '');
    setDiscountPercent(parked.discountPercent || 0);
    setTaxPercent(parked.taxPercent || 11);
    setNotes(parked.notes || '');
    
    // Remove from parked list
    setParkedTransactions(prev => prev.filter(p => p.id !== parked.id));
    setShowParkedList(false);
    
    toast.success('Transaction resumed!');
  };

  // Delete a parked transaction
  const handleDeleteParked = (id) => {
    setParkedTransactions(prev => prev.filter(p => p.id !== id));
    toast.success('Parked transaction deleted');
  };

  // Calculate totals in multiple currencies
  // Tax is only applied to items marked as is_taxable
  const totals = useMemo(() => {
    const subtotalUsd = cart.reduce((sum, item) => sum + (item.line_total_usd || 0), 0);
    // Calculate taxable subtotal - only items where is_taxable is true
    const taxableSubtotal = cart.reduce((sum, item) => {
      if (item.is_taxable !== false) {
        return sum + (item.line_total_usd || 0);
      }
      return sum;
    }, 0);
    const discountAmount = subtotalUsd * (discountPercent / 100);
    const afterDiscount = subtotalUsd - discountAmount;
    // Apply discount proportionally to taxable amount
    const taxableAfterDiscount = taxableSubtotal * (1 - discountPercent / 100);
    // Only calculate tax if VAT registered
    const taxAmount = isVatRegistered() ? taxableAfterDiscount * (taxPercent / 100) : 0;
    const totalUsd = afterDiscount + taxAmount;
    const totalLbp = totalUsd * lbpRate;
    
    // Calculate total cost (purchase price) for all items
    const totalCostUsd = cart.reduce((sum, item) => {
      const itemCost = item.item_cost || 0;
      const qty = item.quantity || 1;
      // If item is in EUR, convert cost to XOF
      if (item.currency === 'EUR') {
        return sum + (itemCost * qty / lbpRate);
      }
      return sum + (itemCost * qty);
    }, 0);
    const totalCostLbp = totalCostUsd * lbpRate;
    
    return { subtotalUsd, taxableSubtotal, discountAmount, taxAmount, totalUsd, totalLbp, totalCostUsd, totalCostLbp };
  }, [cart, discountPercent, taxPercent, lbpRate, isVatRegistered]);

  // Calculate total received in XOF equivalent
  const totalReceivedUsd = useMemo(() => {
    if (paymentMethod === 'customer') return 0;
    const usdAmount = parseFloat(receivedUsd) || 0;
    const lbpAmount = parseFloat(receivedLbp) || 0;
    return usdAmount + (lbpAmount / lbpRate);
  }, [receivedUsd, receivedLbp, lbpRate, paymentMethod]);

  // Calculate remaining balance (positive = still owes, negative = overpaid)
  const remainingBalance = useMemo(() => {
    if (paymentMethod === 'customer') return 0;
    return totals.totalUsd - totalReceivedUsd;
  }, [totals.totalUsd, totalReceivedUsd, paymentMethod]);

  // Auto-calculate change split (prefer EUR to preserve XOF)
  useEffect(() => {
    if (paymentMethod === 'customer' || remainingBalance >= 0) {
      setChangeUsd(0);
      setChangeLbp(0);
      return;
    }
    
    const changeUsdTotal = Math.abs(remainingBalance);
    const changeLbpTotal = Math.round(changeUsdTotal * lbpRate);
    
    if (changeMode === 'lbp') {
      // All change in EUR
      setChangeUsd(0);
      setChangeLbp(changeLbpTotal);
    } else if (changeMode === 'usd') {
      // All change in XOF
      setChangeUsd(changeUsdTotal);
      setChangeLbp(0);
    } else if (changeMode === 'auto') {
      // Smart split: Give change in EUR, rounded to nearest 1000
      // Unless change is more than $5, then give whole dollars in XOF
      if (changeUsdTotal >= 5) {
        const wholeDollars = Math.floor(changeUsdTotal);
        const remainingCents = changeUsdTotal - wholeDollars;
        setChangeUsd(wholeDollars);
        setChangeLbp(Math.round(remainingCents * lbpRate / 1000) * 1000);
      } else {
        setChangeUsd(0);
        setChangeLbp(Math.round(changeLbpTotal / 1000) * 1000);
      }
    }
    // In 'manual' mode, don't auto-update
  }, [remainingBalance, lbpRate, changeMode, paymentMethod]);

  // Calculate the difference after change is given (for discount/premium)
  const finalDifference = useMemo(() => {
    if (paymentMethod === 'customer' || remainingBalance >= 0) return remainingBalance;
    // What customer overpaid in XOF
    const overpaid = Math.abs(remainingBalance);
    // What we're giving back in XOF equivalent
    const changeGiven = changeUsd + (changeLbp / lbpRate);
    // Positive = we gave less change (premium for us), Negative = we gave more change (discount)
    return changeGiven - overpaid;
  }, [remainingBalance, changeUsd, changeLbp, lbpRate, paymentMethod]);

  // Calculate payment adjustment (discount if paid less, premium if paid more)
  const calculatedAdjustment = useMemo(() => {
    if (paymentMethod === 'customer') return 0;
    const paid = parseFloat(paymentAmount) || 0;
    const paidUsd = paymentCurrency === 'XOF' ? paid : paid / paymentExchangeRate;
    // Positive = discount (paid less), Negative = premium (paid more)
    return totals.totalUsd - paidUsd;
  }, [paymentAmount, paymentCurrency, paymentExchangeRate, totals.totalUsd, paymentMethod]);

  // Calculate change (only if paid more than total after adjustment)
  const changeAmount = useMemo(() => {
    if (paymentMethod === 'customer') return 0;
    const paid = parseFloat(paymentAmount) || 0;
    const paidUsd = paymentCurrency === 'XOF' ? paid : paid / paymentExchangeRate;
    // If using adjustment, no change needed
    if (paymentAdjustment !== 0) return 0;
    return Math.max(0, paidUsd - totals.totalUsd);
  }, [paymentAmount, paymentCurrency, paymentExchangeRate, totals.totalUsd, paymentMethod, paymentAdjustment]);

  // Helper function to apply adjustment with threshold check
  const applyAdjustmentWithCheck = (adjustmentAmount) => {
    if (totals.totalUsd === 0) {
      setPaymentAdjustment(adjustmentAmount);
      return;
    }
    const adjustmentPercent = Math.abs(adjustmentAmount) / totals.totalUsd;
    if (adjustmentPercent > ADJUSTMENT_WARNING_THRESHOLD) {
      // Show warning dialog for large adjustments
      setPendingAdjustment(adjustmentAmount);
      setShowAdjustmentWarning(true);
    } else {
      // Apply immediately for small adjustments
      setPaymentAdjustment(adjustmentAmount);
    }
  };

  // Confirm large adjustment
  const confirmLargeAdjustment = () => {
    if (pendingAdjustment !== null) {
      setPaymentAdjustment(pendingAdjustment);
    }
    setPendingAdjustment(null);
    setShowAdjustmentWarning(false);
  };

  // Cancel large adjustment
  const cancelLargeAdjustment = () => {
    setPendingAdjustment(null);
    setShowAdjustmentWarning(false);
  };

  useEffect(() => {
    const loadData = async () => {
      if (!currentOrg) return;
      setLoading(true);
      try {
        const [inventoryRes, cashRes, salesRes, customersRes, summaryRes] = await Promise.all([
          axios.get(`${API}/pos/inventory?organization_id=${currentOrg.id}`),
          axios.get(`${API}/pos/cash-accounts?organization_id=${currentOrg.id}`),
          axios.get(`${API}/sales-accounts?organization_id=${currentOrg.id}`),
          axios.get(`${API}/customer-accounts?organization_id=${currentOrg.id}`),
          axios.get(`${API}/pos/daily-summary?organization_id=${currentOrg.id}`).catch(() => ({ data: null }))
        ]);
        
        const inventoryData = inventoryRes.data;
        
        setInventoryItems(inventoryData);
        const allCashAccounts = cashRes.data;
        setCashAccounts(allCashAccounts.filter(a => a.code.startsWith('5')));
        setBankAccounts(allCashAccounts.filter(a => a.code.startsWith('5')));
        setSalesAccounts(salesRes.data);
        setCustomerAccounts(customersRes.data.filter(a => a.code.startsWith('4111')));
        setDailySummary(summaryRes.data);
        
        if (allCashAccounts.length > 0) {
          const cashAcc = allCashAccounts.find(a => a.code === '5311') || allCashAccounts.find(a => a.code.startsWith('53')) || allCashAccounts[0];
          const bankAcc = allCashAccounts.find(a => a.code === '5111') || allCashAccounts.find(a => a.code.startsWith('51')) || allCashAccounts[0];
          setSelectedCashAccount(cashAcc?.id || allCashAccounts[0].id);
          setSelectedBankAccount(bankAcc?.id || allCashAccounts[0].id);
        }
        if (salesRes.data.length > 0) {
          const salesAcc = salesRes.data.find(a => a.code === '7011') || salesRes.data.find(a => a.code.startsWith('701')) || salesRes.data[0];
          setCreditAccountId(salesAcc?.id || salesRes.data[0].id);
        }
        
        // Get exchange rate from org settings
        if (currentOrg.base_exchange_rate) setLbpRate(currentOrg.base_exchange_rate);
        
        // Load receipt settings
        try {
          const rcptRes = await axios.get(`${API}/receipt-settings?organization_id=${currentOrg.id}`);
          setReceiptSettings(rcptRes.data);
        } catch { /* use defaults */ }
        
        // Get tax rate from org settings
        if (currentOrg.tax_percent !== undefined && currentOrg.tax_percent !== null) {
          setTaxPercent(currentOrg.tax_percent);
        }
        
      } catch (error) {
        console.error('Error loading data:', error);
        if (!error.response && error.message === 'Network Error') {
          alert('Connection Error: Unable to connect to the server. Please check your internet connection.');
        }
      }
      setLoading(false);
    };
    
    loadData();
  }, [currentOrg]);

  // Handle view parameter from URL (for viewing transaction from inventory ledger)
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && transactions.length > 0) {
      const transactionToView = transactions.find(t => t.id === viewId);
      if (transactionToView) {
        setShowReceipt(transactionToView);
        // Clear the view parameter from URL
        searchParams.delete('view');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, transactions, setSearchParams]);

  // Fetch transactions when component mounts to support view parameter
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && currentOrg) {
      fetchTransactions();
    }
  }, [currentOrg, searchParams]);

  const refreshData = async () => {
    if (!currentOrg) return;
    try {
      const summaryRes = await axios.get(`${API}/pos/daily-summary?organization_id=${currentOrg.id}`);
      setDailySummary(summaryRes.data);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await axios.get(`${API}/pos/transactions?organization_id=${currentOrg.id}&limit=50`);
      setTransactions(res.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchCustomerHistory = async (customerId) => {
    if (!customerId) return;
    try {
      const res = await axios.get(`${API}/pos/customer-history?organization_id=${currentOrg.id}&customer_id=${customerId}`);
      setCustomerHistory(res.data);
    } catch (error) {
      console.error('Error fetching customer history:', error);
      setCustomerHistory([]);
    }
  };

  // Fetch today's transactions for Cashier view
  const fetchTodayTransactions = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/pos/transactions?organization_id=${currentOrg.id}&date_from=${today}&date_to=${today}&limit=100`);
      // Filter to only show non-voided transactions for cashier
      setTodayTransactions(res.data.filter(t => !t.is_voided));
    } catch (error) {
      console.error('Error fetching today transactions:', error);
      setTodayTransactions([]);
    }
  };

  // Delete POS transaction and its voucher
  const handleDeleteTransaction = async (transaction) => {
    try {
      setProcessing(true);
      await axios.delete(`${API}/pos/invoices/${transaction.id}?restore_inventory=true`);
      toast.success(`Transaction ${transaction.receipt_number} permanently deleted`);
      setDeleteConfirm(null);
      fetchTransactions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete transaction');
    } finally {
      setProcessing(false);
    }
  };

  const handleVoidTransaction = async () => {
    if (!voidConfirm) return;
    if (!voidReason.trim()) {
      toast.error('Please enter a reason for voiding');
      return;
    }
    try {
      setProcessing(true);
      await axios.put(
        `${API}/pos/invoices/${voidConfirm.id}/void?reason=${encodeURIComponent(voidReason.trim())}`
      );
      toast.success(`Transaction ${voidConfirm.receipt_number} voided`);
      setVoidConfirm(null);
      setVoidReason('');
      fetchTransactions();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to void transaction');
    } finally {
      setProcessing(false);
    }
  };

  // Fetch batches for an inventory item
  const fetchItemBatches = async (itemId) => {
    if (!itemId || itemBatches[itemId]) return itemBatches[itemId];
    try {
      const res = await axios.get(`${API}/inventory/${itemId}/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const availableBatches = (res.data || [])
        .filter(b => b.quantity > 0)
        .sort((a, b) => (a.expiry_date || '9999-12-31').localeCompare(b.expiry_date || '9999-12-31'));
      setItemBatches(prev => ({ ...prev, [itemId]: availableBatches }));
      return availableBatches;
    } catch (error) {
      console.error('Error fetching batches:', error);
      setItemBatches(prev => ({ ...prev, [itemId]: [] }));
      return [];
    }
  };

  // Update cart item batch
  const updateCartBatch = (index, batchId) => {
    setCart(prev => {
      const newCart = [...prev];
      newCart[index] = { ...newCart[index], batch_id: batchId };
      return newCart;
    });
    setBatchSelectorOpen(null);
  };

  // Format expiry date for display
  const formatExpiry = (d) => {
    if (!d) return 'N/A';
    const parts = d.split('-');
    if (parts.length !== 3) return d;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Add item to cart - with controlled substance check
  const addToCart = (item) => {
    // Check if item is controlled substance - show dialog first
    if ((item.is_controlled_substance || item.requires_prescription) && !cart.find(c => c.inventory_item_id === item.id)) {
      setPendingControlledItem(item);
      setControlledFormData({ physician_id: '', patient_id: '', instructions: '', treatment_length_months: 1 });
      setPatientSearchQuery('');
      setPatientsWithPrescription([]);
      setPatientsWithoutPrescription([]);
      
      // Fetch patients grouped by whether they have prescription for this item
      if (currentOrg?.id && item.id) {
        setPatientSearchLoading(true);
        axios.get(`${API}/patients/by-medication/${item.id}?organization_id=${currentOrg.id}`)
          .then(res => {
            setPatientsWithPrescription(res.data.patients_with_prescription || []);
            setPatientsWithoutPrescription(res.data.patients_without_prescription || []);
            // Also update general patients list for fallback
            setPatients([
              ...(res.data.patients_with_prescription || []),
              ...(res.data.patients_without_prescription || [])
            ]);
          })
          .catch(err => {
            console.error('Failed to fetch patients by medication:', err);
            // Fallback to regular patients fetch
            axios.get(`${API}/patients?organization_id=${currentOrg.id}`)
              .then(patRes => {
                setPatients(Array.isArray(patRes.data) ? patRes.data : []);
                setPatientsWithoutPrescription(Array.isArray(patRes.data) ? patRes.data : []);
              });
          })
          .finally(() => setPatientSearchLoading(false));
        
        // Also fetch physicians
        axios.get(`${API}/physicians?organization_id=${currentOrg.id}`)
          .then(physRes => {
            setPhysicians(Array.isArray(physRes.data) ? physRes.data : []);
          })
          .catch(err => console.error('Failed to fetch physicians:', err));
      }
      
      // Reset all prescription-related state
      setSelectedPrescription(null);
      setSelectedPatientPrescriptions([]);
      
      setShowControlledDialog(true);
      return;
    }
    
    addItemToCartDirect(item);
  };
  
  // Search patients for controlled substance dialog
  const handlePatientSearch = async (searchTerm) => {
    setPatientSearchQuery(searchTerm);
    
    if (!currentOrg?.id || !pendingControlledItem?.id) return;
    
    try {
      const res = await axios.get(
        `${API}/patients/by-medication/${pendingControlledItem.id}?organization_id=${currentOrg.id}&search=${encodeURIComponent(searchTerm)}`
      );
      setPatientsWithPrescription(res.data.patients_with_prescription || []);
      setPatientsWithoutPrescription(res.data.patients_without_prescription || []);
    } catch (err) {
      console.error('Failed to search patients:', err);
    }
  };

  // Add item directly to cart (bypasses controlled substance check)
  const addItemToCartDirect = (item, prescriptionInfo = null) => {
    // Fetch batches if expiry tracking is enabled
    if (currentOrg?.enable_expiry_tracking) {
      fetchItemBatches(item.id).then(batches => {
        if (batches && batches.length > 0) {
          setCart(prev => prev.map(c => 
            c.inventory_item_id === item.id && !c.batch_id
              ? { ...c, batch_id: batches[0].id }
              : c
          ));
        }
      });
    }
    
    setCart(prev => {
      const existing = prev.find(c => c.inventory_item_id === item.id);
      if (existing) {
        return prev.map(c => 
          c.inventory_item_id === item.id 
            ? { 
                ...c, 
                quantity: c.quantity + 1,
                line_total: (c.quantity + 1) * c.unit_price * (1 - c.discount_percent / 100),
                line_total_usd: ((c.quantity + 1) * c.unit_price * (1 - c.discount_percent / 100)) / (c.exchange_rate || 1)
              }
            : c
        );
      }
      
      const lineTotal = item.price;
      const exchangeRate = item.currency === 'EUR' ? lbpRate : 1;
      const lineTotalUsd = item.currency === 'XOF' ? lineTotal : lineTotal / exchangeRate;
      
      return [...prev, {
        inventory_item_id: item.id,
        item_name: item.name,
        item_name_ar: item.name_ar,
        barcode: item.barcode,
        quantity: 1,
        unit: item.unit || 'piece',
        unit_price: item.price,
        item_cost: item.cost || 0,
        currency: item.currency || 'XOF',
        exchange_rate: exchangeRate,
        discount_percent: 0,
        line_total: lineTotal,
        line_total_usd: lineTotalUsd,
        is_taxable: item.is_taxable !== false,
        batch_id: '',
        is_controlled_substance: item.is_controlled_substance || false,
        requires_prescription: item.requires_prescription || false,
        prescription_info: prescriptionInfo
      }];
    });
  };

  // State for early pickup warning
  const [earlyPickupWarning, setEarlyPickupWarning] = useState(null);

  // Handle patient selection - lookup prescriptions for this patient + medication
  const handlePatientSelect = async (patientId) => {
    setControlledFormData(prev => ({ ...prev, patient_id: patientId }));
    setSelectedPrescription(null);
    setSelectedPatientPrescriptions([]);
    
    if (!patientId || !pendingControlledItem) return;
    
    setPrescriptionLookupLoading(true);
    try {
      const res = await axios.get(`${API}/prescription-register/patient-prescriptions`, {
        params: {
          organization_id: currentOrg?.id,
          patient_id: patientId,
          medication_id: pendingControlledItem?.id
        }
      });
      
      const data = res.data || {};
      if (data.has_valid_prescription && Array.isArray(data.prescriptions) && data.prescriptions.length > 0) {
        setSelectedPatientPrescriptions(data.prescriptions);
        // Auto-select the most recent valid prescription
        const prescription = data.prescriptions[0];
        setSelectedPrescription(prescription);
        // Auto-fill form data from prescription
        setControlledFormData(prev => ({
          ...prev,
          physician_id: prescription?.physician_id || '',
          instructions: prescription?.instructions || '',
          treatment_length_months: prescription?.treatment_length_months || 1
        }));
      } else {
        setSelectedPatientPrescriptions([]);
        setSelectedPrescription(null);
      }
    } catch (error) {
      console.error('Failed to lookup patient prescriptions:', error);
      toast.error('Failed to lookup prescriptions: ' + (error?.response?.data?.detail || error.message));
      setSelectedPatientPrescriptions([]);
      setSelectedPrescription(null);
    } finally {
      setPrescriptionLookupLoading(false);
    }
  };

  // Handle controlled substance dialog submission - ONLY if valid prescription exists
  const handleControlledSubmit = async () => {
    if (!controlledFormData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    
    if (!selectedPrescription) {
      toast.error('No valid prescription found. Please register a prescription first.');
      return;
    }
    
    // Check for early pickup
    try {
      const checkRes = await axios.get(`${API}/prescription-register/check-early-pickup`, {
        params: {
          organization_id: currentOrg.id,
          patient_id: controlledFormData.patient_id,
          medication_id: pendingControlledItem?.id
        }
      });
      
      if (checkRes.data.has_active_prescription) {
        // Show warning but allow to proceed
        const nextPickup = checkRes.data.prescriptions[0];
        setEarlyPickupWarning({
          patient_name: patients.find(p => p.id === controlledFormData.patient_id)?.name || 'Patient',
          medication_name: pendingControlledItem?.name || 'medication',
          next_window_start: nextPickup?.pickup_window_start,
          prescriptions: checkRes.data.prescriptions
        });
        return; // Don't add yet, show warning first
      }
    } catch (error) {
      console.error('Failed to check early pickup:', error);
      // Continue anyway if check fails
    }
    
    // No warning needed, proceed normally
    completeControlledSubmit();
  };
  
  // Complete the controlled submission after warning acknowledged
  const completeControlledSubmit = () => {
    const prescriptionInfo = {
      prescription_id: selectedPrescription?.id,
      physician_id: selectedPrescription?.physician_id || controlledFormData.physician_id,
      physician_name: selectedPrescription?.physician_name || physicians.find(p => p.id === controlledFormData.physician_id)?.name || '',
      patient_id: controlledFormData.patient_id,
      patient_name: patients.find(p => p.id === controlledFormData.patient_id)?.name || '',
      instructions: selectedPrescription?.instructions || controlledFormData.instructions,
      treatment_length_months: selectedPrescription?.treatment_length_months || controlledFormData.treatment_length_months || 1
    };
    
    addItemToCartDirect(pendingControlledItem, prescriptionInfo);
    setShowControlledDialog(false);
    setPendingControlledItem(null);
    setEarlyPickupWarning(null);
    setSelectedPrescription(null);
    setSelectedPatientPrescriptions([]);
    toast.success('Controlled item added with prescription');
  };
  
  // Handle early pickup warning - proceed anyway
  const handleProceedWithEarlyPickup = () => {
    setEarlyPickupWarning(null);
    completeControlledSubmit();
  };
  
  // Handle early pickup warning - cancel
  const handleCancelEarlyPickup = () => {
    setEarlyPickupWarning(null);
  };

  // Update cart item quantity (allows negative for returns)
  const updateQuantity = (index, delta) => {
    setCart(prev => {
      const newCart = [...prev];
      const item = newCart[index];
      const newQty = item.quantity + delta;
      
      // Don't allow zero - either positive (sale) or negative (return)
      if (newQty === 0) return prev;
      
      const lineTotal = newQty * item.unit_price * (1 - item.discount_percent / 100);
      const lineTotalUsd = item.currency === 'XOF' ? lineTotal : lineTotal / (item.exchange_rate || 1);
      
      newCart[index] = { ...item, quantity: newQty, line_total: lineTotal, line_total_usd: lineTotalUsd };
      
      // If quantity just became negative, open batch selector for returns
      if (newQty < 0 && item.quantity > 0) {
        setReturnBatchSelector({ index, item: newCart[index] });
      }
      
      return newCart;
    });
  };

  // State for return batch selector
  const [returnBatchSelector, setReturnBatchSelector] = useState(null);

  // Set return quantity directly (e.g., from input)
  const setQuantityDirect = (index, qty) => {
    const newQty = parseInt(qty) || 0;
    if (newQty === 0) return;
    
    setCart(prev => {
      const newCart = [...prev];
      const item = newCart[index];
      const lineTotal = newQty * item.unit_price * (1 - item.discount_percent / 100);
      const lineTotalUsd = item.currency === 'XOF' ? lineTotal : lineTotal / (item.exchange_rate || 1);
      
      newCart[index] = { ...item, quantity: newQty, line_total: lineTotal, line_total_usd: lineTotalUsd };
      
      // If quantity is negative and no return_batch_id set, open batch selector
      if (newQty < 0 && !item.return_batch_id) {
        setReturnBatchSelector({ index, item: newCart[index] });
      }
      
      return newCart;
    });
  };

  // Update return batch for a return item
  const updateReturnBatch = (index, batchId, batchNumber, expiryDate) => {
    setCart(prev => {
      const newCart = [...prev];
      newCart[index] = { 
        ...newCart[index], 
        return_batch_id: batchId,
        return_batch_number: batchNumber,
        return_expiry_date: expiryDate
      };
      return newCart;
    });
    setReturnBatchSelector(null);
  };

  // Update cart item discount
  const updateItemDiscount = (index, discount) => {
    setCart(prev => {
      const newCart = [...prev];
      const item = newCart[index];
      const discPct = Math.min(100, Math.max(0, parseFloat(discount) || 0));
      const lineTotal = item.quantity * item.unit_price * (1 - discPct / 100);
      const lineTotalUsd = item.currency === 'XOF' ? lineTotal : lineTotal / (item.exchange_rate || 1);
      
      newCart[index] = { ...item, discount_percent: discPct, line_total: lineTotal, line_total_usd: lineTotalUsd };
      return newCart;
    });
  };

  // Remove from cart
  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
    setDiscountPercent(0);
    setSelectedCustomerId('');
    setNotes('');
    setPaymentAmount('');
    setPaymentMethod('cash');
    setPaymentAdjustment(0);
  };

  // Handle barcode input
  const handleBarcodeSubmit = (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    
    const item = inventoryItems.find(i => 
      i.barcode?.toLowerCase() === barcodeInput.trim().toLowerCase()
    );
    
    if (item) {
      addToCart(item);
      setBarcodeInput('');
    } else {
      alert('Item not found');
    }
  };

  // Handle camera barcode scan result
  const handleCameraScan = (decodedText) => {
    setShowCameraScanner(false);
    const item = inventoryItems.find(i =>
      i.barcode?.toLowerCase() === decodedText.trim().toLowerCase()
    );
    if (item) {
      addToCart(item);
      toast.success(`Added: ${item.name}`);
    } else {
      setBarcodeInput(decodedText);
      toast.error(`Item not found for barcode: ${decodedText}`);
    }
  };

  // Get debit account based on payment method
  const getDebitAccountId = () => {
    switch (paymentMethod) {
      case 'cash':
        return selectedCashAccount;
      case 'card':
        return selectedBankAccount;
      case 'customer':
        return selectedCustomerId;
      default:
        return selectedCashAccount;
    }
  };

  // Process payment
  const processPayment = async () => {
    if (cart.length === 0) return;
    
    const debitAccountId = getDebitAccountId();
    if (!debitAccountId || !creditAccountId) {
      alert('Please configure payment and sales accounts');
      return;
    }
    
    if (paymentMethod === 'customer' && !selectedCustomerId) {
      alert('Please select a customer for credit sale');
      return;
    }
    
    setProcessing(true);
    try {
      const selectedCustomer = customerAccounts.find(c => c.id === selectedCustomerId);
      const transactionId = 'offline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const receiptNumber = 'POS-' + new Date().getFullYear() + '-' + String(Date.now()).slice(-5);
      
      // Calculate change amount for receipt
      const totalChangeUsd = changeUsd + (changeLbp / lbpRate);
      
      const payload = {
        organization_id: currentOrg.id,
        lines: cart,
        subtotal_usd: totals.subtotalUsd,
        discount_percent: discountPercent,
        discount_amount: totals.discountAmount,
        tax_percent: taxPercent,
        tax_amount: totals.taxAmount,
        total_usd: totals.totalUsd,
        total_lbp: totals.totalLbp,
        payment_method: paymentMethod,
        // Dual currency payment data
        received_usd: paymentMethod === 'customer' ? totals.totalUsd : (parseFloat(receivedUsd) || 0),
        received_lbp: paymentMethod === 'customer' ? 0 : (parseFloat(receivedLbp) || 0),
        payment_amount: paymentMethod === 'customer' ? totals.totalUsd : totalReceivedUsd,
        payment_currency: 'DUAL', // Indicates dual-currency payment
        payment_exchange_rate: lbpRate,
        change_usd: changeUsd,
        change_lbp: changeLbp,
        change_amount: totalChangeUsd,
        payment_adjustment: -paymentAdjustment, // Negate: Frontend uses (-) for discount, Backend expects (+) for discount
        customer_id: selectedCustomerId || null,
        customer_name: selectedCustomer?.name || (paymentMethod === 'customer' ? null : 'Walk-in'),
        customer_code: selectedCustomer?.code || null,
        notes: notes || null,
        debit_account_id: debitAccountId,
        credit_account_id: creditAccountId,
        lbp_rate: lbpRate
      };
      
      let transactionData;
      
      const res = await axios.post(`${API}/pos/transactions`, payload);
      transactionData = res.data;
      
      // Create prescription register entries for controlled substances
      const controlledItems = cart.filter(item => item.prescription_info);
      if (controlledItems.length > 0) {
        try {
          const prescriptionEntries = controlledItems.map(item => ({
            organization_id: currentOrg.id,
            date: new Date().toISOString().split('T')[0],
            physician_id: item.prescription_info.physician_id,
            patient_id: item.prescription_info.patient_id,
            medication_id: item.inventory_item_id,
            medication_name: item.item_name,
            instructions: item.prescription_info.instructions || '',
            quantity: item.quantity,
            treatment_length_months: item.prescription_info.treatment_length_months || 1,
            pos_transaction_id: transactionData.id,
            batch_id: item.batch_id || ''
          }));
          
          await axios.post(`${API}/prescription-register/bulk`, prescriptionEntries);
          const totalEntries = prescriptionEntries.reduce((sum, e) => sum + (e.treatment_length_months || 1), 0);
          toast.success(`${totalEntries} prescription pickup entry(s) added to register`);
        } catch (rxError) {
          console.error('Failed to create prescription records:', rxError);
          toast.error('Transaction complete but failed to add prescription records');
        }
      }
      
      setShowPayment(false);
      setShowReceipt(transactionData);
      clearCart();
      
      // Refresh data
      refreshData();
      
    } catch (error) {
      console.error('Error processing payment:', error);
      alert(error.response?.data?.detail || 'Error processing payment');
    }
    setProcessing(false);
  };

  // Print thermal receipt (POS printer format)
  const printThermalReceipt = (transaction) => {
    const rs = receiptSettings || {};
    const pw = rs.printer_width || '80mm';
    const fs = rs.font_size || '12px';
    const storeName = rs.store_name || currentOrg?.name || 'KAIROS POS';
    const storeNameAr = rs.store_name_ar || '';
    const addr1 = rs.address_line1 || currentOrg?.address || '';
    const addr2 = rs.address_line2 || '';
    const phone = rs.phone || currentOrg?.phone || '';
    const vatNum = rs.vat_number || '';
    const footerMsg = rs.footer_message || 'Thank you for your business!';
    const footerMsgAr = rs.footer_message_ar || '';
    const showLogo = rs.show_logo !== false && rs.logo_url;
    const showVat = rs.show_vat_number !== false && vatNum;
    const showBarcode = rs.show_barcode !== false;

    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${transaction.receipt_number}</title>
        <style>
          /* THERMAL PRINTER - MAXIMUM DARKNESS + LARGER FONTS */
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body { 
            font-family: 'Courier New', Courier, monospace; 
            font-size: 14pt; /* INCREASED base font */
            width: ${pw}; 
            padding: 4mm;
            background: #fff !important;
            color: #000000 !important;
            font-weight: 900 !important;
            -webkit-font-smoothing: none;
            text-rendering: geometricPrecision;
            letter-spacing: 0.3px;
          }
          /* MAXIMUM DARKNESS: Bold + stroke, NO shadow */
          body, p, span, div, td, th, h1, h2, h3, h4, h5, h6 { 
            color: #000000 !important; 
            font-weight: 900 !important;
            -webkit-text-stroke: 0.7px #000000;
          }
          .header { text-align: center; margin-bottom: 4mm; }
          .header h1 { 
            font-size: 18pt; /* LARGE store name */
            font-weight: 900 !important; 
            margin-bottom: 2mm; 
            letter-spacing: 1.5px;
            text-transform: uppercase;
          }
          .header h2 { font-size: 15pt; font-weight: 900 !important; margin-bottom: 2mm; }
          .header p { font-size: 12pt; font-weight: 900 !important; line-height: 1.4; }
          .logo { text-align: center; margin-bottom: 3mm; }
          .logo img { max-height: 55px; max-width: 80%; filter: contrast(3) brightness(0.3) grayscale(1); }
          .divider { 
            border: none;
            border-top: 3px dashed #000000 !important; 
            margin: 4mm 0; 
            height: 0;
          }
          .divider-double { 
            border: none;
            border-top: 5px solid #000000 !important; 
            margin: 4mm 0;
            height: 0;
          }
          .info-row { 
            display: flex; 
            justify-content: space-between; 
            font-size: 12pt; /* INCREASED */
            margin: 2mm 0; 
            font-weight: 900 !important; 
          }
          .item { margin: 3mm 0; }
          .item-name { font-size: 13pt; font-weight: 900 !important; } /* INCREASED */
          .item-details { 
            display: flex; 
            justify-content: space-between; 
            font-size: 12pt; /* INCREASED */
            color: #000000 !important; 
            font-weight: 900 !important; 
          }
          .total-section { margin-top: 3mm; }
          .total-row { 
            display: flex; 
            justify-content: space-between; 
            font-size: 13pt; /* INCREASED */
            margin: 2mm 0; 
            font-weight: 900 !important; 
          }
          .grand-total { 
            font-size: 16pt; /* LARGE total */
            font-weight: 900 !important;
            letter-spacing: 1px;
            border: 2px solid #000 !important;
            padding: 2mm;
            background: #ddd !important;
          }
          .payment-info { 
            background: #ccc !important; /* Darker gray for thermal */
            padding: 3mm; 
            margin: 4mm 0; 
            border: 3px solid #000000 !important;
            font-weight: 900 !important;
          }
          .footer { 
            text-align: center; 
            margin-top: 4mm; 
            font-size: 12pt; /* INCREASED */
            font-weight: 900 !important; 
          }
          .barcode-area { 
            text-align: center; 
            padding: 4mm; 
            border: 3px solid #000000 !important; 
            margin: 4mm 0; 
            font-size: 11pt; 
            font-weight: 900 !important;
            background: #fff !important;
          }
          .barcode-area svg { 
            filter: contrast(5) !important; /* Max barcode darkness */
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body { 
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              font-size: 14pt !important;
            }
            body, p, span, div, td, th, h1, h2, h3 {
              color: #000000 !important;
              font-weight: 900 !important;
              -webkit-text-stroke: 0.7px #000 !important;
            }
            .header h1 { font-size: 18pt !important; }
            .header h2 { font-size: 15pt !important; }
            .grand-total { font-size: 16pt !important; }
            .payment-info { background: #ccc !important; border-width: 3px !important; }
            .barcode-area svg { filter: contrast(5) !important; }
          }
        </style>
      </head>
      <body>
        ${showLogo ? `<div class="logo"><img src="${rs.logo_url}" alt="Logo" /></div>` : ''}
        
        <div class="header">
          <h1>${storeName}</h1>
          ${storeNameAr ? `<h2 dir="rtl">${storeNameAr}</h2>` : ''}
          ${addr1 ? `<p>${addr1}</p>` : ''}
          ${addr2 ? `<p>${addr2}</p>` : ''}
          ${phone ? `<p>Tel: ${phone}</p>` : ''}
          ${showVat ? `<p>VAT: ${vatNum}</p>` : ''}
        </div>
        
        <div class="divider-double"></div>
        
        <div class="info-row">
          <span>Receipt #:</span>
          <span>${transaction.receipt_number}</span>
        </div>
        <div class="info-row">
          <span>Date:</span>
          <span>${(() => { const d = new Date(transaction.date); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })()} ${transaction.time}</span>
        </div>
        <div class="info-row">
          <span>Cashier:</span>
          <span>${transaction.cashier_name || 'Admin'}</span>
        </div>
        ${transaction.customer_name ? `
        <div class="info-row">
          <span>Customer:</span>
          <span>${transaction.customer_code ? `[${transaction.customer_code}] ` : ''}${transaction.customer_name}</span>
        </div>
        ` : ''}
        
        <div class="divider"></div>
        
        ${transaction.lines.map(line => `
          <div class="item">
            <div class="item-name">${line.item_name}</div>
            <div class="item-details">
              <span>${line.quantity} x ${line.currency} ${parseFloat(line.unit_price).toFixed(3)}${line.discount_percent > 0 ? ` (-${line.discount_percent}%)` : ''}</span>
              <span>$${(line.line_total_usd || line.line_total).toFixed(3)}</span>
            </div>
          </div>
        `).join('')}
        
        <div class="divider"></div>
        
        <div class="total-section">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>$${transaction.subtotal_usd.toFixed(3)}</span>
          </div>
          ${transaction.discount_amount > 0 ? `
          <div class="total-row">
            <span>Discount (${transaction.discount_percent}%):</span>
            <span>-$${transaction.discount_amount.toFixed(3)}</span>
          </div>
          ` : ''}
          ${transaction.tax_amount > 0 ? `
          <div class="total-row">
            <span>VAT (${transaction.tax_percent}%):</span>
            <span>+$${transaction.tax_amount.toFixed(3)}</span>
          </div>
          ` : ''}
        </div>
        
        <div class="divider-double"></div>
        
        <div class="total-row grand-total">
          <span>TOTAL XOF:</span>
          <span>$${transaction.total_usd.toFixed(3)}</span>
        </div>
        ${transaction.total_lbp ? `
        <div class="total-row">
          <span>TOTAL EUR:</span>
          <span>€ ${Math.round(transaction.total_lbp).toLocaleString()}</span>
        </div>
        ` : ''}
        
        <div class="divider"></div>
        
        <div class="payment-info">
          <div class="total-row">
            <span>Payment (${transaction.payment_method.toUpperCase()}):</span>
            <span>${transaction.payment_currency} ${parseFloat(transaction.payment_amount).toFixed(3)}</span>
          </div>
          ${transaction.change_amount > 0 ? `
          <div class="total-row">
            <span>Change:</span>
            <span>$${transaction.change_amount.toFixed(3)}</span>
          </div>
          ` : ''}
        </div>
        
        <div class="divider"></div>
        
        <div class="footer">
          <p>Voucher: ${transaction.voucher_number || 'N/A'}</p>
          ${footerMsg ? `<p style="margin-top: 2mm;">${footerMsg}</p>` : ''}
          ${footerMsgAr ? `<p dir="rtl" style="margin-top: 1mm;">${footerMsgAr}</p>` : ''}
        </div>
        
        ${showBarcode ? `
        <div class="barcode-area" style="padding: 3mm; border: 2px solid #000; margin: 3mm 0;">
          <div style="text-align: center; margin-bottom: 2mm;">
            <svg id="barcode128"></svg>
          </div>
          <div style="text-align: center; font-size: 0.8em; font-weight: 700; margin: 1mm 0;">
            ${transaction.receipt_number}
          </div>
          <div style="text-align: center; margin-top: 3mm;">
            <div id="qrcode" style="display: inline-block;"></div>
          </div>
        </div>
        
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <script src="https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js"></script>
        <script>
          // Generate Code 128 barcode (more compact than Code 39)
          try {
            JsBarcode("#barcode128", "${transaction.receipt_number}", {
              format: "CODE128",
              width: 1.5,
              height: 40,
              displayValue: false,
              margin: 0
            });
          } catch(e) { console.log('Barcode error:', e); }
          
          // Generate QR Code
          try {
            var qr = qrcode(0, 'M');
            qr.addData("${transaction.receipt_number}");
            qr.make();
            document.getElementById('qrcode').innerHTML = qr.createSvgTag(2, 0);
          } catch(e) { console.log('QR error:', e); }
        </script>
        ` : ''}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=320,height=700');
    printWindow.document.write(receiptHtml);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Format amount based on display currency
  const formatAmount = (usdAmount) => {
    if (displayCurrency === 'EUR') {
      return `€ ${Math.round(usdAmount * lbpRate).toLocaleString()}`;
    }
    return `${formatUSD(usdAmount)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if quick items panel should be shown
  const showQuickItems = currentOrg?.pos_quick_items_enabled !== false;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row gap-4 p-4 pb-20 lg:pb-4">
      {/* Left Panel - Products (only if quick items enabled) */}
      {showQuickItems && (
      <div className={`flex-1 flex-col gap-4 min-w-0 ${mobileTab === 'products' ? 'flex' : 'hidden lg:flex'}`}>
        {/* Search & Barcode */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Barcode Input */}
              <form onSubmit={handleBarcodeSubmit} className="flex-1">
                <div className="relative flex gap-1">
                  <div className="relative flex-1">
                    <BarChart3 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      ref={barcodeInputRef}
                      placeholder="Scan barcode..."
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      className="pl-10 h-12 text-lg font-mono"
                      autoFocus
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-12 w-12 shrink-0 border-cyan-500/40 text-cyan-500 hover:bg-cyan-500/10"
                    onClick={() => setShowCameraScanner(true)}
                    data-testid="pos-camera-scan-btn"
                    title="Scan with Camera"
                  >
                    <Camera className="w-5 h-5" />
                  </Button>
                </div>
              </form>
              
              {/* Search Dropdown */}
              <div className="flex-1 flex items-center gap-1">
                <div className="flex-1">
                  <POSItemSelector items={inventoryItems} onSelect={addToCart} organizationId={currentOrg?.id} />
                </div>
                <Button variant="outline" size="icon" className="h-12 w-12 shrink-0"
                  onClick={() => { setQaItemMode('add'); setQaItemEdit(null); setQaItemOpen(true); }}
                  data-testid="pos-sidebar-add-item-btn" title="Add New Item">
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Items Grid */}
        <Card className="flex-1 overflow-hidden">
          <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4" />
              Quick Items
            </CardTitle>
            {/* Display Currency Toggle */}
            <div className="flex items-center gap-2">
              <Button 
                variant={displayCurrency === 'XOF' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setDisplayCurrency('XOF')}
                className="h-7 px-2"
              >
                XOF
              </Button>
              <Button 
                variant={displayCurrency === 'EUR' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setDisplayCurrency('EUR')}
                className="h-7 px-2"
              >
                EUR
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 overflow-y-auto h-[calc(100%-3rem)]">
            {inventoryItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <Package className="w-12 h-12 mb-2 opacity-50" />
                <p>No inventory items found</p>
                <p className="text-sm">Add items from the Inventory page</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {/* Show items marked as POS items first, then configured quick items, then first 20 */}
                {(() => {
                  const posItems = inventoryItems.filter(item => item.is_pos_item);
                  const configuredItems = currentOrg?.pos_quick_items?.length > 0 
                    ? inventoryItems.filter(item => currentOrg.pos_quick_items.includes(item.id) && !item.is_pos_item)
                    : [];
                  const quickItems = posItems.length > 0 || configuredItems.length > 0
                    ? [...posItems, ...configuredItems]
                    : inventoryItems.slice(0, 20);
                  return quickItems.length > 0 ? quickItems.map(item => (
                    <QuickItemButton key={item.id} item={item} onClick={addToCart} showImage={true} />
                  )) : (
                    <div className="col-span-full text-center text-muted-foreground py-8">
                      <p>No quick items configured</p>
                      <p className="text-sm">Mark items as &quot;POS Quick Item&quot; in Inventory, or add items in Organization Settings</p>
                    </div>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Right Panel - Cart (full width when quick items disabled) */}
      {showQuickItems ? (
        /* === STANDARD SIDEBAR CART VIEW === */
        <div className={`flex-col gap-4 w-full lg:w-[420px] xl:w-[480px] ${mobileTab === 'cart' ? 'flex' : 'hidden lg:flex'}`}>
          {/* Daily Summary */}
          {!isCashier() && (
          <Card className="bg-gradient-to-r from-green-500/10 to-cyan-500/10">
            <CardContent className="p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Today&apos;s Sales
                </span>
                <span className="font-bold text-green-400">
                  {formatUSD(dailySummary?.total_sales || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
                <span>{dailySummary?.total_transactions || 0} transactions</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={() => { setShowHistory(true); fetchTransactions(); }}
                >
                  <History className="w-3 h-3 mr-1" />
                  History
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={() => setShowReceiptSettings(true)}
                  data-testid="receipt-settings-btn"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Receipt
                </Button>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Customer Selection */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <CustomerSelector 
                    customers={customerAccounts} 
                    value={selectedCustomerId}
                    onChange={(id) => {
                      setSelectedCustomerId(id);
                      if (id) {
                        setPaymentMethod('customer');
                        fetchCustomerHistory(id);
                      } else {
                        setPaymentMethod('cash');
                      }
                    }}
                    organizationId={currentOrg?.id}
                    apiUrl={API}
                  />
                </div>
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"
                  onClick={() => { setQaCustomerMode('add'); setQaCustomerEdit(null); setQaCustomerOpen(true); }}
                  data-testid="pos-sidebar-add-customer-btn" title="Add New Customer">
                  <Plus className="w-4 h-4" />
                </Button>
                {selectedCustomerId && (
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"
                    onClick={() => {
                      const cust = customerAccounts.find(c => c.id === selectedCustomerId);
                      if (cust) { setQaCustomerMode('edit'); setQaCustomerEdit(cust); setQaCustomerOpen(true); }
                    }}
                    data-testid="pos-sidebar-edit-customer-btn" title="Edit Customer">
                    <UserCheck className="w-4 h-4" />
                  </Button>
                )}
                {selectedCustomerId && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setShowCustomerHistory(true); fetchCustomerHistory(selectedCustomerId); }}
                  >
                    <History className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cart Items - Sidebar View */}
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" />
                Cart ({cart.length} items)
              </CardTitle>
              <div className="flex items-center gap-1">
                {/* Prescription Label Button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowPrescriptionLabel(true)} 
                  className="h-7 text-green-500 border-green-500/30 hover:bg-green-500/10"
                  data-testid="pos-prescription-label-btn"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  Label
                </Button>
                {/* Parked Transactions Button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowParkedList(true)} 
                  className="h-7 text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                  data-testid="pos-parked-btn"
                >
                  <Clock className="w-3 h-3 mr-1" />
                  Parked {parkedTransactions.length > 0 && `(${parkedTransactions.length})`}
                </Button>
                {/* Park Button */}
                {cart.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleParkTransaction} 
                    className="h-7 text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                    data-testid="pos-park-btn"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Park
                  </Button>
                )}
                {/* Clear Button */}
                {cart.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-400 h-7">
                    <Trash2 className="w-3 h-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-y-auto p-0">
              {cart.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mb-2 opacity-20" />
                  <p>Cart is empty</p>
                  <p className="text-xs">Scan a barcode or search for items</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {cart.map((item, idx) => {
                    const isReturn = item.quantity < 0;
                    return (
                    <div key={idx} className={`p-3 hover:bg-muted/30 ${isReturn ? 'bg-red-500/10 border-l-4 border-red-500' : ''}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div className={`font-medium truncate ${isReturn ? 'text-red-400' : ''}`}>{item.item_name}</div>
                            {isReturn && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded font-bold">RETURN</span>
                            )}
                          </div>
                          <div className={`text-xs ${isReturn ? 'text-red-400/70' : 'text-muted-foreground'}`}>
                            {item.currency} {formatUSD(item.unit_price)} × {item.quantity}
                            {item.discount_percent > 0 && (
                              <span className="text-red-400 ml-1">(-{item.discount_percent}%)</span>
                            )}
                          </div>
                          {/* Return batch info */}
                          {isReturn && item.return_batch_id && (
                            <div className="text-[10px] text-orange-400 mt-1">
                              Return to: {item.return_batch_number || 'Batch'} | Exp: {formatExpiry(item.return_expiry_date)}
                            </div>
                          )}
                          {/* Sale batch selector (for positive qty) */}
                          {!isReturn && currentOrg?.enable_expiry_tracking && itemBatches[item.inventory_item_id]?.length > 0 && (
                            <div className="mt-1 relative">
                              {/* Show selected batch info */}
                              {item.batch_id && (() => {
                                const batch = itemBatches[item.inventory_item_id]?.find(b => b.id === item.batch_id);
                                return batch ? (
                                  <span className="text-[10px] text-blue-400 font-mono mr-1">
                                    {batch.batch_number} | Exp: {formatExpiry(batch.expiry_date)}
                                  </span>
                                ) : null;
                              })()}
                              <button
                                onClick={(e) => { e.stopPropagation(); setBatchSelectorOpen(batchSelectorOpen === idx ? null : idx); }}
                                className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
                              >
                                {item.batch_id ? 'Manual' : 'Auto (FEFO)'}
                              </button>
                              {batchSelectorOpen === idx && (
                                <div className="absolute left-0 top-full mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px]">
                                  <div className="text-[10px] text-gray-500 mb-1 font-medium">Select Batch:</div>
                                  <div className="space-y-1 max-h-40 overflow-y-auto">
                                    <button
                                      onClick={() => { updateCartBatch(idx, ''); setBatchSelectorOpen(null); }}
                                      className={`w-full text-left text-xs px-2 py-1.5 rounded ${!item.batch_id ? 'bg-purple-100 text-purple-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                    >
                                      Auto (FEFO) - Earliest expiry first
                                    </button>
                                    <div className="border-t border-gray-100 my-1" />
                                    {itemBatches[item.inventory_item_id]?.map(batch => (
                                      <button
                                        key={batch.id}
                                        onClick={() => { updateCartBatch(idx, batch.id); setBatchSelectorOpen(null); }}
                                        className={`w-full text-left text-xs px-2 py-1.5 rounded ${item.batch_id === batch.id ? 'bg-amber-100 text-amber-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                      >
                                        <div className="font-mono">{batch.batch_number}</div>
                                        <div className="text-[10px] text-gray-400">
                                          Qty: {batch.quantity} | Exp: {formatExpiry(batch.expiry_date)}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className={`font-mono font-bold ${isReturn ? 'text-red-400' : 'text-green-400'}`}>
                            {isReturn ? '-' : ''}{formatAmount(Math.abs(item.line_total_usd))}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className={`flex items-center rounded ${isReturn ? 'bg-red-500/20' : 'bg-muted'}`}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => updateQuantity(idx, -1)}
                          >
                            <Minus className="w-3 h-3" />
                          </Button>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => setQuantityDirect(idx, e.target.value)}
                            className={`h-7 w-12 text-center font-mono border-0 bg-transparent ${isReturn ? 'text-red-400' : ''}`}
                          />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 w-7 p-0"
                            onClick={() => updateQuantity(idx, 1)}
                          >
                            <Plus className="w-3 h-3" />
                          </Button>
                        </div>
                        {!isReturn && (
                          <div className="flex items-center gap-1">
                            <Percent className="w-3 h-3 text-muted-foreground" />
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={item.discount_percent}
                              onChange={(e) => updateItemDiscount(idx, e.target.value)}
                              className="h-7 w-14 text-xs text-center"
                            />
                          </div>
                        )}
                        {isReturn && !item.return_batch_id && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-7 text-xs text-orange-500 border-orange-500/50"
                            onClick={() => {
                              fetchItemBatches(item.inventory_item_id);
                              setReturnBatchSelector({ index: idx, item });
                            }}
                          >
                            Set Batch
                          </Button>
                        )}
                        {/* Return Toggle Button */}
                        <Button 
                          variant={isReturn ? "destructive" : "ghost"}
                          size="sm" 
                          className={`h-7 px-2 text-xs ${isReturn ? '' : 'text-orange-500 hover:bg-orange-50'}`}
                          onClick={() => {
                            const newQty = isReturn ? 1 : -1;
                            setQuantityDirect(idx, newQty.toString());
                            if (!isReturn) {
                              fetchItemBatches(item.inventory_item_id);
                              setReturnBatchSelector({ index: idx, item: {...item, quantity: -1} });
                            }
                          }}
                          title={isReturn ? "Cancel Return" : "Return Item"}
                        >
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 w-7 p-0 text-red-400 ml-auto"
                          onClick={() => removeFromCart(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>

            {/* Totals */}
            <div className="border-t p-4 bg-muted/30 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span className={`font-mono ${totals.subtotalUsd < 0 ? 'text-red-400' : ''}`}>
                  {totals.subtotalUsd < 0 ? '-' : ''}{formatAmount(Math.abs(totals.subtotalUsd))}
                </span>
              </div>
              {totals.subtotalUsd >= 0 && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Discount:</span>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={discountPercent}
                      onChange={(e) => setDiscountPercent(Math.min(100, parseFloat(e.target.value) || 0))}
                      className="h-7 w-16 text-xs text-center"
                    />
                    <span className="text-sm">%</span>
                    <span className="ml-auto font-mono text-red-400">-{formatAmount(totals.discountAmount)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Tax:</span>
                    <Input
                      type="number"
                      min="0"
                      value={taxPercent}
                      onChange={(e) => setTaxPercent(parseFloat(e.target.value) || 0)}
                      className="h-7 w-16 text-xs text-center"
                    />
                    <span className="text-sm">%</span>
                    <span className="ml-auto font-mono text-cyan-400">+{formatAmount(totals.taxAmount)}</span>
                  </div>
                </>
              )}
              <div className="border-t pt-2 space-y-1">
                {totals.totalUsd < 0 ? (
                  <>
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-red-500">REFUND XOF:</span>
                      <span className="font-mono text-red-500">{formatUSD(Math.abs(totals.totalUsd))}</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span className="text-red-400">REFUND EUR:</span>
                      <span className="font-mono text-red-400">€ {Math.round(Math.abs(totals.totalLbp)).toLocaleString()}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between text-xl font-bold">
                      <span>TOTAL XOF:</span>
                      <span className="font-mono text-green-400">{formatUSD(totals.totalUsd)}</span>
                    </div>
                    <div className="flex justify-between text-lg">
                      <span>TOTAL EUR:</span>
                      <span className="font-mono text-cyan-400">€ {Math.round(totals.totalLbp).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment Button */}
            <div className="p-4 border-t">
              <Button 
                className={`w-full h-14 text-lg font-bold ${totals.totalUsd < 0 ? 'bg-red-600 hover:bg-red-700' : ''}`}
                disabled={cart.length === 0}
                onClick={() => {
                  // Reset dual-currency payment state
                  setReceivedUsd('');
                  setReceivedLbp('');
                  setChangeUsd(0);
                  setChangeLbp(0);
                  setChangeMode('auto');
                  setPaymentAdjustment(0);
                  setShowPayment(true);
                }}
              >
                {totals.totalUsd < 0 ? (
                  <>
                    <RotateCcw className="w-5 h-5 mr-2" />
                    Process Refund {formatUSD(Math.abs(totals.totalUsd))}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Pay {formatUSD(totals.totalUsd)}
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      ) : (
        /* === FULL-SCREEN POS VIEW (No Quick Items) === */
        <div className="flex-1 flex flex-col gap-3 min-h-0">
          {/* Top Bar: Search + Customer + Summary (always visible on desktop, products tab on mobile) */}
          <div className={`flex-col lg:flex-row flex-wrap items-stretch gap-3 ${mobileTab === 'products' ? 'flex' : 'hidden lg:flex'}`}>
            {/* Barcode Input */}
            <form onSubmit={handleBarcodeSubmit} className="w-full lg:w-72">
              <div className="flex gap-1">
                <div className="relative flex-1">
                  <BarChart3 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    ref={barcodeInputRef}
                    placeholder="Scan barcode..."
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    className="pl-9 h-10 font-mono"
                    autoFocus
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 border-cyan-500/40 text-cyan-500 hover:bg-cyan-500/10"
                  onClick={() => setShowCameraScanner(true)}
                  data-testid="pos-fullscreen-camera-scan-btn"
                  title="Scan with Camera"
                >
                  <Camera className="w-4 h-4" />
                </Button>
              </div>
            </form>
            
            {/* Search Dropdown */}
            <div className="w-full lg:w-72 flex items-center gap-1">
              <div className="flex-1">
                <POSItemSelector items={inventoryItems} onSelect={addToCart} organizationId={currentOrg?.id} />
              </div>
              <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"
                onClick={() => { setQaItemMode('add'); setQaItemEdit(null); setQaItemOpen(true); }}
                data-testid="pos-bar-add-item-btn" title="Add New Item">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Customer Selector */}
            <div className="w-full lg:w-64 flex items-center gap-1">
              <User className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <CustomerSelector 
                  customers={customerAccounts} 
                  value={selectedCustomerId}
                  onChange={(id) => {
                    setSelectedCustomerId(id);
                    if (id) {
                      setPaymentMethod('customer');
                      fetchCustomerHistory(id);
                    } else {
                      setPaymentMethod('cash');
                    }
                  }}
                  organizationId={currentOrg?.id}
                  apiUrl={API}
                />
              </div>
              <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"
                onClick={() => { setQaCustomerMode('add'); setQaCustomerEdit(null); setQaCustomerOpen(true); }}
                data-testid="pos-bar-add-customer-btn" title="Add New Customer">
                <Plus className="w-4 h-4" />
              </Button>
              {selectedCustomerId && (
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0"
                  onClick={() => {
                    const cust = customerAccounts.find(c => c.id === selectedCustomerId);
                    if (cust) { setQaCustomerMode('edit'); setQaCustomerEdit(cust); setQaCustomerOpen(true); }
                  }}
                  data-testid="pos-bar-edit-customer-btn" title="Edit Customer">
                  <UserCheck className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Currency Toggle */}
            <div className="flex items-center gap-1 ml-auto">
              <Button 
                variant={displayCurrency === 'XOF' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setDisplayCurrency('XOF')}
                className="h-10 px-3"
              >
                XOF
              </Button>
              <Button 
                variant={displayCurrency === 'EUR' ? 'default' : 'outline'} 
                size="sm" 
                onClick={() => setDisplayCurrency('EUR')}
                className="h-10 px-3"
              >
                EUR
              </Button>
            </div>
            
            {/* Daily Summary Compact - For Admin/Super Admin */}
            {!isCashier() && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-gradient-to-r from-green-500/10 to-cyan-500/10 border">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Today</div>
                <div className="font-bold text-green-400">{formatUSD(dailySummary?.total_sales || 0)}</div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 px-2"
                onClick={() => { setShowHistory(true); fetchTransactions(); }}
              >
                <History className="w-4 h-4" />
              </Button>
            </div>
            )}
            
            {/* Cashier Today's Sales Button - No Amount Shown */}
            {isCashier() && (
            <div className="flex items-center">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-9 px-3 gap-2 bg-gradient-to-r from-green-500/10 to-cyan-500/10 border-green-500/30"
                onClick={() => { setShowCashierHistory(true); fetchTodayTransactions(); }}
                data-testid="cashier-history-btn"
              >
                <Clock className="w-4 h-4 text-green-400" />
                <span className="text-sm">Today's Sales</span>
              </Button>
            </div>
            )}
          </div>

          {/* Main Content: Cart Table + Totals Panel (always visible on desktop, cart tab on mobile) */}
          <div className={`flex-1 gap-3 min-h-0 ${mobileTab === 'cart' ? 'flex flex-col lg:flex-row' : 'hidden lg:flex'}`}>
            {/* Cart Table - Takes most of the space */}
            <Card className="flex-1 flex flex-col overflow-hidden min-h-[200px]">
              <CardHeader className="py-2 px-4 border-b flex-row items-center justify-between shrink-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Cart ({cart.length} items)
                </CardTitle>
                <div className="flex items-center gap-1">
                  {/* Prescription Label Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowPrescriptionLabel(true)} 
                    className="h-7 text-xs text-green-500 border-green-500/30 hover:bg-green-500/10"
                    data-testid="pos-prescription-label-btn-fullscreen"
                  >
                    <Tag className="w-3 h-3 mr-1" />
                    Label
                  </Button>
                  {/* Parked Transactions Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowParkedList(true)} 
                    className="h-7 text-xs text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
                    data-testid="pos-parked-btn-fullscreen"
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Parked {parkedTransactions.length > 0 && `(${parkedTransactions.length})`}
                  </Button>
                  {/* Park Button */}
                  {cart.length > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleParkTransaction} 
                      className="h-7 text-xs text-blue-400 border-blue-400/30 hover:bg-blue-400/10"
                      data-testid="pos-park-btn-fullscreen"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Park
                    </Button>
                  )}
                  {/* Clear Button */}
                  {cart.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-400 h-7 text-xs">
                      <Trash2 className="w-3 h-3 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
              </CardHeader>
              
              <div className="flex-1 overflow-auto">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-12">
                    <ShoppingCart className="w-16 h-16 mb-3 opacity-20" />
                    <p className="text-lg">Cart is empty</p>
                    <p className="text-sm">Scan a barcode or search for items to begin</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr className="text-left text-xs text-muted-foreground">
                        <th className="py-2 px-3 font-medium w-8">#</th>
                        <th className="py-2 px-3 font-medium">Item</th>
                        <th className="py-2 px-3 font-medium text-center w-24">Price</th>
                        <th className="py-2 px-3 font-medium text-center w-32">Qty</th>
                        <th className="py-2 px-3 font-medium text-center w-20">Disc%</th>
                        <th className="py-2 px-3 font-medium text-right w-28">Total</th>
                        <th className="py-2 px-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {cart.map((item, idx) => {
                        const isReturn = item.quantity < 0;
                        return (
                        <tr key={idx} className={`hover:bg-muted/30 group ${isReturn ? 'bg-red-500/10' : ''}`}>
                          <td className="py-2 px-3 text-muted-foreground text-xs">{idx + 1}</td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-2">
                              <div className={`font-medium truncate max-w-[280px] ${isReturn ? 'text-red-400' : ''}`}>{item.item_name}</div>
                              {isReturn && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded font-bold">RETURN</span>
                              )}
                            </div>
                            {item.barcode && <div className="text-xs text-cyan-400 font-mono">{item.barcode}</div>}
                            {/* Item cost in parentheses - same currency as item */}
                            {item.item_cost > 0 && (
                              <div className="text-xs text-red-400 font-mono">
                                ({item.currency === 'EUR' ? item.item_cost.toLocaleString() : item.item_cost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})})
                              </div>
                            )}
                            {/* Return batch info */}
                            {isReturn && item.return_batch_id && (
                              <div className="text-[10px] text-orange-400 mt-1">
                                Return to: {item.return_batch_number || 'Batch'} | Exp: {formatExpiry(item.return_expiry_date)}
                              </div>
                            )}
                            {isReturn && !item.return_batch_id && (
                              <button
                                onClick={() => {
                                  fetchItemBatches(item.inventory_item_id);
                                  setReturnBatchSelector({ index: idx, item });
                                }}
                                className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded hover:bg-orange-500/30 transition-colors mt-1"
                              >
                                Set Return Batch
                              </button>
                            )}
                            {/* Sale batch selector (for positive qty only) */}
                            {!isReturn && currentOrg?.enable_expiry_tracking && itemBatches[item.inventory_item_id]?.length > 0 && (
                              <div className="mt-1 relative">
                                {/* Show selected batch info */}
                                {item.batch_id && (() => {
                                  const batch = itemBatches[item.inventory_item_id]?.find(b => b.id === item.batch_id);
                                  return batch ? (
                                    <span className="text-[10px] text-blue-400 font-mono mr-1">
                                      {batch.batch_number} | Exp: {formatExpiry(batch.expiry_date)}
                                    </span>
                                  ) : null;
                                })()}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setBatchSelectorOpen(batchSelectorOpen === idx ? null : idx); }}
                                  className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
                                >
                                  {item.batch_id ? 'Manual' : 'Auto (FEFO)'}
                                </button>
                                {batchSelectorOpen === idx && (
                                  <div className="absolute left-0 top-full mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px]">
                                    <div className="text-[10px] text-gray-500 mb-1 font-medium">Select Batch:</div>
                                    <div className="space-y-1 max-h-40 overflow-y-auto">
                                      <button
                                        onClick={() => { updateCartBatch(idx, ''); setBatchSelectorOpen(null); }}
                                        className={`w-full text-left text-xs px-2 py-1.5 rounded ${!item.batch_id ? 'bg-purple-100 text-purple-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                      >
                                        Auto (FEFO) - Earliest expiry first
                                      </button>
                                      <div className="border-t border-gray-100 my-1" />
                                      {itemBatches[item.inventory_item_id]?.map(batch => (
                                        <button
                                          key={batch.id}
                                          onClick={() => { updateCartBatch(idx, batch.id); setBatchSelectorOpen(null); }}
                                          className={`w-full text-left text-xs px-2 py-1.5 rounded ${item.batch_id === batch.id ? 'bg-amber-100 text-amber-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}
                                        >
                                          <div className="font-mono">{batch.batch_number}</div>
                                          <div className="text-[10px] text-gray-400">
                                            Qty: {batch.quantity} | Exp: {formatExpiry(batch.expiry_date)}
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center font-mono text-xs">
                            <div className={isReturn ? 'text-red-400' : ''}>
                              {item.currency} {formatUSD(item.is_taxable !== false ? item.unit_price * (1 + taxPercent / 100) : item.unit_price)}
                              {item.is_taxable !== false && !isReturn && (
                                <div className="text-[9px] text-cyan-400 font-normal">TTC</div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => updateQuantity(idx, -1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <Input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => setQuantityDirect(idx, e.target.value)}
                                className={`h-7 w-14 text-center font-mono font-medium ${isReturn ? 'text-red-400 bg-red-500/10' : ''}`}
                              />
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="h-7 w-7 p-0"
                                onClick={() => updateQuantity(idx, 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="py-2 px-3">
                            {!isReturn && (
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={item.discount_percent}
                                onChange={(e) => updateItemDiscount(idx, e.target.value)}
                                className="h-7 w-16 text-xs text-center mx-auto"
                              />
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            <span className={`font-mono font-bold ${isReturn ? 'text-red-400' : 'text-green-400'}`}>
                              {isReturn ? '-' : ''}{formatAmount(Math.abs(item.line_total_usd))}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <div className="flex items-center gap-1">
                              {/* Return Toggle Button */}
                              <Button 
                                variant={isReturn ? "destructive" : "outline"}
                                size="sm" 
                                className={`h-7 px-2 text-xs ${isReturn ? '' : 'text-orange-500 border-orange-300 hover:bg-orange-50'}`}
                                onClick={() => {
                                  const newQty = isReturn ? 1 : -1;
                                  setQuantityDirect(idx, newQty.toString());
                                  if (!isReturn) {
                                    fetchItemBatches(item.inventory_item_id);
                                    setReturnBatchSelector({ index: idx, item: {...item, quantity: -1} });
                                  }
                                }}
                                title={isReturn ? "Cancel Return" : "Return Item"}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                {isReturn ? 'Sale' : 'Return'}
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 w-7 p-0 text-red-400 opacity-50 group-hover:opacity-100"
                                onClick={() => removeFromCart(idx)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </Card>

            {/* Totals & Payment Panel - Fixed width on right, full width on mobile */}
            <Card className="w-full lg:w-72 flex flex-col shrink-0">
              <CardHeader className="py-2 px-4 border-b shrink-0">
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              
              <CardContent className="flex-1 p-4 space-y-3 overflow-auto">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono">{formatAmount(totals.subtotalUsd)}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Discount</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={discountPercent}
                    onChange={(e) => setDiscountPercent(Math.min(100, parseFloat(e.target.value) || 0))}
                    className="h-7 w-14 text-xs text-center ml-auto"
                  />
                  <span className="text-xs">%</span>
                </div>
                {totals.discountAmount > 0 && (
                  <div className="text-right text-sm font-mono text-red-400">
                    -{formatAmount(totals.discountAmount)}
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Tax</span>
                  <Input
                    type="number"
                    min="0"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(parseFloat(e.target.value) || 0)}
                    className="h-7 w-14 text-xs text-center ml-auto"
                  />
                  <span className="text-xs">%</span>
                </div>
                {totals.taxAmount > 0 && (
                  <div className="text-right text-sm font-mono text-cyan-400">
                    +{formatAmount(totals.taxAmount)}
                  </div>
                )}
                
                {/* Total Cost TTC - in red */}
                {totals.totalCostUsd > 0 && (
                  <div className="border-t pt-3 space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-red-500 font-medium">Cost XOF</span>
                      <span className="font-mono text-red-500">
                        ${formatUSD(totals.totalCostUsd)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-red-500 font-medium">Cost EUR</span>
                      <span className="font-mono text-red-500">
                        € {Math.round(totals.totalCostLbp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
                
                <div className="border-t pt-3 space-y-2">
                  {totals.totalUsd < 0 ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-red-500">REFUND XOF</span>
                        <span className="text-2xl font-bold font-mono text-red-500">
                          ${formatUSD(Math.abs(totals.totalUsd))}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-red-400">REFUND EUR</span>
                        <span className="font-mono text-red-400">
                          € {Math.round(Math.abs(totals.totalLbp)).toLocaleString()}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total XOF</span>
                        <span className="text-2xl font-bold font-mono text-green-400">
                          ${formatUSD(totals.totalUsd)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total EUR</span>
                        <span className="text-xl font-bold font-mono text-cyan-400">
                          € {Math.round(totals.totalLbp).toLocaleString()}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>

              {/* Payment Button */}
              <div className="p-4 border-t mt-auto shrink-0 space-y-2">
                <Button 
                  variant="outline"
                  className="w-full h-10 text-sm font-semibold text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
                  disabled={cart.length < 2}
                  onClick={() => setShowInteractions(true)}
                  data-testid="pos-check-interactions-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>
                  Check Interactions
                </Button>
                <Button 
                  className={`w-full h-12 text-base font-bold ${totals.totalUsd < 0 ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  disabled={cart.length === 0}
                  onClick={() => {
                    // Reset dual-currency payment state
                    setReceivedUsd('');
                    setReceivedLbp('');
                    setChangeUsd(0);
                    setChangeLbp(0);
                    setChangeMode('auto');
                    setPaymentAdjustment(0);
                    setShowPayment(true);
                  }}
                >
                  {totals.totalUsd < 0 ? (
                    <>
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Process Refund ${formatUSD(Math.abs(totals.totalUsd))}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pay Now
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Mobile Bottom Tab Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border flex lg:hidden z-50">
        <button 
          onClick={() => setMobileTab('products')}
          className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors ${mobileTab === 'products' ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`}
          data-testid="pos-mobile-tab-products"
        >
          <LayoutGrid className="w-5 h-5" />
          <span className="text-[10px] font-medium">Products</span>
        </button>
        <button 
          onClick={() => setMobileTab('cart')}
          className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors relative ${mobileTab === 'cart' ? 'text-primary bg-primary/10' : 'text-muted-foreground'}`}
          data-testid="pos-mobile-tab-cart"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="text-[10px] font-medium">Cart</span>
          {cart.length > 0 && (
            <span className="absolute top-1.5 right-[calc(50%-2px)] translate-x-3 bg-primary text-primary-foreground text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {cart.length}
            </span>
          )}
        </button>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Payment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Total Display */}
            <div className={`text-center p-4 rounded-lg ${totals.totalUsd < 0 ? 'bg-red-500/10' : 'bg-muted'}`}>
              <div className="text-sm text-muted-foreground">{totals.totalUsd < 0 ? 'Refund Amount' : 'Amount Due'}</div>
              <div className={`text-3xl font-bold font-mono ${totals.totalUsd < 0 ? 'text-red-500' : 'text-green-400'}`}>
                {totals.totalUsd < 0 ? '-' : ''}${formatUSD(Math.abs(totals.totalUsd))}
              </div>
              <div className={`text-lg font-mono ${totals.totalUsd < 0 ? 'text-red-400' : 'text-cyan-400'}`}>
                {totals.totalUsd < 0 ? '-' : ''}€ {Math.round(Math.abs(totals.totalLbp)).toLocaleString()}
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  type="button"
                  variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('cash')}
                  className="h-14 flex-col gap-1"
                >
                  <Banknote className="w-5 h-5" />
                  <span className="text-xs">Cash</span>
                </Button>
                <Button 
                  type="button"
                  variant={paymentMethod === 'card' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('card')}
                  className="h-14 flex-col gap-1"
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="text-xs">Card/Bank</span>
                </Button>
                <Button 
                  type="button"
                  variant={paymentMethod === 'customer' ? 'default' : 'outline'}
                  onClick={() => setPaymentMethod('customer')}
                  disabled={!selectedCustomerId}
                  className="h-14 flex-col gap-1"
                >
                  <UserCheck className="w-5 h-5" />
                  <span className="text-xs">On Account</span>
                </Button>
              </div>
            </div>

            {/* Customer Credit Info */}
            {paymentMethod === 'customer' && selectedCustomerId && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <UserCheck className="w-4 h-4 text-blue-400" />
                  <span>Charge to: </span>
                  <span className="font-bold">
                    {customerAccounts.find(c => c.id === selectedCustomerId)?.name}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Current Balance: ${formatUSD(customerAccounts.find(c => c.id === selectedCustomerId)?.balance_usd || 0)}
                </div>
              </div>
            )}

            {/* Cash/Card Payment Details */}
            {paymentMethod !== 'customer' && (
              <>
                {/* Account Selection */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">{paymentMethod === 'cash' ? 'Cash Account' : 'Bank Account'}</Label>
                    <Select 
                      value={paymentMethod === 'cash' ? selectedCashAccount : selectedBankAccount} 
                      onValueChange={(v) => paymentMethod === 'cash' ? setSelectedCashAccount(v) : setSelectedBankAccount(v)}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(paymentMethod === 'cash' ? cashAccounts : bankAccounts).map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-xs">
                            {a.code} - {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Sales Account</Label>
                    <Select value={creditAccountId} onValueChange={setCreditAccountId}>
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {salesAccounts.map(a => (
                          <SelectItem key={a.id} value={a.id} className="text-xs">
                            {a.code} - {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Dual Currency Amount Received */}
                <div className="space-y-3">
                  <Label className="flex items-center gap-2 text-sm font-medium">
                    <Banknote className="w-4 h-4 text-green-400" />
                    Amount Received
                  </Label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {/* XOF Received */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">XOF</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={receivedUsd}
                        onChange={(e) => setReceivedUsd(e.target.value)}
                        className="h-10 text-lg font-mono text-center border-green-500/30 focus:border-green-500"
                        data-testid="received-usd"
                      />
                    </div>
                    {/* EUR Received */}
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">EUR</Label>
                      <Input
                        type="number"
                        step="1000"
                        min="0"
                        placeholder="0"
                        value={receivedLbp}
                        onChange={(e) => setReceivedLbp(e.target.value)}
                        className="h-10 text-lg font-mono text-center border-cyan-500/30 focus:border-cyan-500"
                        data-testid="received-lbp"
                      />
                    </div>
                  </div>

                  {/* Quick Amount Buttons */}
                  {paymentMethod === 'cash' && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {[1, 2, 5, 10, 20, 50, 100].map(amt => (
                          <Button 
                            key={`usd-${amt}`} 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setReceivedUsd((prev) => (parseFloat(prev) || 0) + amt + '')}
                            className="h-7 px-2 text-xs text-green-400 border-green-500/30 hover:bg-green-500/10"
                          >
                            +${amt}
                          </Button>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[10000, 50000, 100000, 500000, 1000000, 2000000].map(amt => (
                          <Button 
                            key={`lbp-${amt}`} 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setReceivedLbp((prev) => (parseFloat(prev) || 0) + amt + '')}
                            className="h-7 px-2 text-xs text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/10"
                          >
                            +{amt >= 1000000 ? `${amt/1000000}M` : `${amt/1000}K`}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Total Received Summary */}
                  <div className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                    <span className="text-muted-foreground">Total Received:</span>
                    <span className="font-mono font-bold">
                      ${formatUSD(totalReceivedUsd)} 
                      <span className="text-xs text-muted-foreground ml-1">
                        (€ {Math.round(totalReceivedUsd * lbpRate).toLocaleString()})
                      </span>
                    </span>
                  </div>
                </div>

                {/* Balance Status */}
                {totalReceivedUsd > 0 && (
                  <div className={`p-3 rounded-lg text-center ${
                    remainingBalance > 0.01 
                      ? 'bg-red-500/10 border border-red-500/30' 
                      : remainingBalance < -0.01 
                        ? 'bg-green-500/10 border border-green-500/30'
                        : 'bg-emerald-500/10 border border-emerald-500/30'
                  }`}>
                    {remainingBalance > 0.01 ? (
                      <>
                        <div className="text-sm text-muted-foreground">Still Owes</div>
                        <div className="text-xl font-bold text-red-400 font-mono">
                          ${formatUSD(remainingBalance)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          € {Math.round(remainingBalance * lbpRate).toLocaleString()}
                        </div>
                        {/* Apply as Discount Button */}
                        {paymentAdjustment === 0 && (
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            className="mt-2 text-orange-400 border-orange-500/30 hover:bg-orange-500/10"
                            onClick={() => applyAdjustmentWithCheck(-remainingBalance)}
                            data-testid="apply-discount-btn"
                          >
                            <Percent className="w-3 h-3 mr-1" />
                            Apply as Discount
                          </Button>
                        )}
                      </>
                    ) : remainingBalance < -0.01 ? (
                      <>
                        <div className="text-sm text-muted-foreground">Overpaid - Change Due</div>
                        <div className="text-xl font-bold text-green-400 font-mono">
                          ${formatUSD(Math.abs(remainingBalance))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          € {Math.round(Math.abs(remainingBalance) * lbpRate).toLocaleString()}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-emerald-400 font-medium flex items-center justify-center gap-1">
                          <Check className="w-4 h-4" /> Exact Amount
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Change Due Section - Only show if overpaid */}
                {remainingBalance < -0.01 && (
                  <div className="space-y-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label className="flex items-center gap-2 text-sm font-medium text-yellow-400">
                        <Coins className="w-4 h-4" />
                        Change to Give
                      </Label>
                      <div className="flex gap-1">
                        <Button 
                          type="button" 
                          variant={changeMode === 'auto' ? 'secondary' : 'ghost'}
                          size="sm" 
                          className="h-6 text-xs px-2"
                          onClick={() => setChangeMode('auto')}
                        >
                          Auto
                        </Button>
                        <Button 
                          type="button" 
                          variant={changeMode === 'lbp' ? 'secondary' : 'ghost'}
                          size="sm" 
                          className="h-6 text-xs px-2"
                          onClick={() => setChangeMode('lbp')}
                        >
                          All EUR
                        </Button>
                        <Button 
                          type="button" 
                          variant={changeMode === 'usd' ? 'secondary' : 'ghost'}
                          size="sm" 
                          className="h-6 text-xs px-2"
                          onClick={() => setChangeMode('usd')}
                        >
                          All XOF
                        </Button>
                        <Button 
                          type="button" 
                          variant={changeMode === 'manual' ? 'secondary' : 'ghost'}
                          size="sm" 
                          className="h-6 text-xs px-2"
                          onClick={() => setChangeMode('manual')}
                        >
                          Manual
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Change XOF */}
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Give XOF</Label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          value={changeUsd}
                          onChange={(e) => {
                            setChangeMode('manual');
                            setChangeUsd(parseFloat(e.target.value) || 0);
                          }}
                          className="h-10 text-lg font-mono text-center border-yellow-500/30 focus:border-yellow-500"
                          data-testid="change-usd"
                        />
                      </div>
                      {/* Change EUR */}
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Give EUR</Label>
                        <Input
                          type="number"
                          step="1000"
                          min="0"
                          value={changeLbp}
                          onChange={(e) => {
                            setChangeMode('manual');
                            setChangeLbp(parseFloat(e.target.value) || 0);
                          }}
                          className="h-10 text-lg font-mono text-center border-yellow-500/30 focus:border-yellow-500"
                          data-testid="change-lbp"
                        />
                      </div>
                    </div>

                    {/* Difference after change */}
                    {Math.abs(finalDifference) > 0.005 && paymentAdjustment === 0 && (
                      <div className={`p-2 rounded text-center text-sm ${
                        finalDifference > 0 
                          ? 'bg-purple-500/10 border border-purple-500/30' 
                          : 'bg-orange-500/10 border border-orange-500/30'
                      }`}>
                        <div className="text-xs text-muted-foreground">
                          {finalDifference > 0 ? 'Giving less change (Premium for you)' : 'Giving more change (Discount for customer)'}
                        </div>
                        <div className={`font-bold font-mono ${finalDifference > 0 ? 'text-purple-400' : 'text-orange-400'}`}>
                          {finalDifference > 0 ? '+' : ''}{formatUSD(finalDifference)} XOF
                        </div>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          className="mt-1.5 h-7"
                          onClick={() => applyAdjustmentWithCheck(finalDifference)}
                        >
                          Apply as {finalDifference > 0 ? 'Premium' : 'Discount'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Applied Adjustment Display */}
                {paymentAdjustment !== 0 && (
                  <div className={`p-3 rounded-lg ${paymentAdjustment < 0 ? 'bg-green-500/10 border border-green-500/30' : 'bg-purple-500/10 border border-purple-500/30'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-muted-foreground">
                          {paymentAdjustment < 0 ? 'Discount Applied' : 'Premium Applied'}
                        </div>
                        <div className={`text-lg font-bold font-mono ${paymentAdjustment < 0 ? 'text-green-400' : 'text-purple-400'}`}>
                          {paymentAdjustment < 0 ? '-' : '+'}${formatUSD(Math.abs(paymentAdjustment))}
                        </div>
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400"
                        onClick={() => setPaymentAdjustment(0)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPayment(false)}>
              Cancel
            </Button>
            <Button 
              onClick={processPayment}
              disabled={processing || (paymentMethod !== 'customer' && remainingBalance > 0.01 && paymentAdjustment === 0)}
              className="min-w-[140px]"
            >
              {processing ? 'Processing...' : (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  Complete Sale
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Large Adjustment Warning Dialog */}
      <Dialog open={showAdjustmentWarning} onOpenChange={setShowAdjustmentWarning}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <AlertTriangle className="w-5 h-5" />
              Large Adjustment Warning
            </DialogTitle>
          </DialogHeader>
          
          {pendingAdjustment !== null && (
            <div className="space-y-4">
              <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <div className="text-center">
                  <div className="text-sm text-muted-foreground mb-2">
                    You are about to apply a
                  </div>
                  <div className="text-2xl font-bold text-orange-400 font-mono">
                    {pendingAdjustment < 0 ? '-' : '+'}${formatUSD(Math.abs(pendingAdjustment))} {pendingAdjustment < 0 ? 'Discount' : 'Premium'}
                  </div>
                  <div className="text-lg font-semibold text-orange-300 mt-1">
                    ({totals.totalUsd > 0 ? ((Math.abs(pendingAdjustment) / totals.totalUsd) * 100).toFixed(1) : 0}% of total)
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-muted rounded-lg text-center">
                <div className="text-sm text-muted-foreground">This will change the sale from</div>
                <div className="flex items-center justify-center gap-3 mt-2">
                  <span className="text-lg font-mono line-through text-muted-foreground">${formatUSD(totals.totalUsd)}</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  <span className="text-lg font-mono font-bold text-green-400">
                    ${formatUSD(totals.totalUsd - Math.abs(pendingAdjustment))}
                  </span>
                </div>
              </div>

              <div className="text-sm text-center text-muted-foreground">
                Are you sure you want to proceed with this {pendingAdjustment < 0 ? 'discount' : 'premium'}?
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelLargeAdjustment}>
              Cancel
            </Button>
            <Button 
              onClick={confirmLargeAdjustment}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Check className="w-4 h-4 mr-1" />
              Confirm Adjustment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={!!showReceipt} onOpenChange={() => setShowReceipt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <Check className="w-5 h-5" />
              Sale Complete!
            </DialogTitle>
          </DialogHeader>
          
          {showReceipt && (
            <div className="space-y-4">
              <div className="text-center p-4 bg-muted rounded-lg">
                <div className="font-mono text-lg font-bold">{showReceipt.receipt_number}</div>
                <div className="text-2xl font-bold text-green-400 font-mono my-2">
                  ${formatUSD(showReceipt.total_usd)}
                </div>
                <div className="text-lg text-cyan-400 font-mono">
                  € {Math.round(showReceipt.total_lbp || showReceipt.total_usd * lbpRate).toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground mt-2">
                  {formatDate(showReceipt.date)} {showReceipt.time}
                </div>
              </div>

              {(showReceipt.change_amount > 0 || showReceipt.change_usd > 0 || showReceipt.change_lbp > 0) && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-center">
                  <div className="text-sm text-muted-foreground">Change Given</div>
                  <div className="flex justify-center gap-4 mt-1">
                    {(showReceipt.change_usd > 0 || showReceipt.change_lbp > 0) ? (
                      <>
                        {showReceipt.change_usd > 0 && (
                          <div className="text-lg font-bold text-green-400 font-mono">
                            ${formatUSD(showReceipt.change_usd)} XOF
                          </div>
                        )}
                        {showReceipt.change_lbp > 0 && (
                          <div className="text-lg font-bold text-cyan-400 font-mono">
                            € {Math.round(showReceipt.change_lbp).toLocaleString()}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-xl font-bold text-yellow-400 font-mono">
                        ${formatUSD(showReceipt.change_amount)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Details */}
              {(showReceipt.received_usd > 0 || showReceipt.received_lbp > 0) && (
                <div className="p-3 bg-muted/50 rounded text-sm">
                  <div className="text-muted-foreground text-center mb-2">Amount Received</div>
                  <div className="flex justify-center gap-4">
                    {showReceipt.received_usd > 0 && (
                      <div className="font-mono text-green-400">
                        ${formatUSD(showReceipt.received_usd)} XOF
                      </div>
                    )}
                    {showReceipt.received_lbp > 0 && (
                      <div className="font-mono text-cyan-400">
                        € {Math.round(showReceipt.received_lbp).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="text-sm text-center space-y-1">
                <div className="text-muted-foreground">
                  Payment: <span className="text-foreground capitalize">{showReceipt.payment_method}</span>
                </div>
                <div className="text-muted-foreground">
                  Voucher: <span className="text-foreground font-mono">{showReceipt.voucher_number}</span>
                </div>
                {showReceipt.customer_name && (
                  <div className="text-muted-foreground">
                    Customer: <span className="text-foreground">{showReceipt.customer_name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReceipt(null)}>
              Close
            </Button>
            <Button onClick={() => printThermalReceipt(showReceipt)}>
              <Printer className="w-4 h-4 mr-1" />
              Print Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Transaction History
            </DialogTitle>
            <DialogDescription className="flex items-center gap-3">
              <span>{transactions.filter(t => !t.is_voided).length} active transactions</span>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={showVoided}
                  onChange={e => setShowVoided(e.target.checked)}
                  className="rounded border-border"
                />
                Show voided ({transactions.filter(t => t.is_voided).length})
              </label>
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh]">
            {transactions.filter(t => showVoided || !t.is_voided).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No transactions found
              </div>
            ) : (
              <table className="data-table text-sm" data-testid="history-table">
                <thead>
                  <tr>
                    <th>Receipt #</th>
                    <th>Date/Time</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th className="text-right">Total</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.filter(t => showVoided || !t.is_voided).map(t => (
                    <tr 
                      key={t.id} 
                      className={`${t.is_voided ? 'opacity-50' : ''} cursor-pointer hover:bg-muted/50 transition-colors`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewHistoryTransaction(t);
                      }}
                    >
                      <td className={`font-mono text-xs ${t.is_voided ? 'line-through' : ''}`}>{t.receipt_number}</td>
                      <td className="text-muted-foreground text-xs">{formatDate(t.date)} {t.time}</td>
                      <td className="text-xs">{t.customer_name || '-'}</td>
                      <td>{t.lines.length}</td>
                      <td className={`text-right font-mono font-bold ${t.is_voided ? 'text-red-400 line-through' : 'text-green-400'}`}>
                        ${formatUSD(t.total_usd)}
                      </td>
                      <td className="capitalize text-xs">{t.payment_method}</td>
                      <td>
                        {t.is_voided ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400 border border-red-500/30" title={`Reason: ${t.void_reason || 'N/A'}\nBy: ${t.voided_by_name || 'Admin'}\nAt: ${t.voided_at || ''}`}>
                            VOIDED
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                            Active
                          </span>
                        )}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setViewHistoryTransaction(t)} title="View Details">
                            <Eye className="w-3 h-3" />
                          </Button>
                          {!t.is_voided && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => printThermalReceipt(t)} title="Print Receipt">
                                <Printer className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/20" 
                                onClick={() => { setVoidConfirm(t); setVoidReason(''); }}
                                title="Void Transaction"
                                data-testid="void-btn"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20" 
                            onClick={() => setDeleteConfirm(t)}
                            title={t.is_voided ? "Permanently Delete" : "Delete Transaction"}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Transaction Detail View Dialog (for super admin history) */}
      <Dialog open={!!viewHistoryTransaction} onOpenChange={(open) => !open && setViewHistoryTransaction(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] z-[60]">
          {viewHistoryTransaction && (
            <>
              <DialogHeader className="border-b pb-3">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Receipt className="w-5 h-5 text-primary" />
                  <span className="font-mono">{viewHistoryTransaction.receipt_number}</span>
                  {viewHistoryTransaction.is_voided && (
                    <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
                      VOIDED
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription className="text-xs">Transaction Details</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-2 overflow-y-auto max-h-[60vh]">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span className="font-medium">{formatDate(viewHistoryTransaction.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    <span className="font-medium">{viewHistoryTransaction.time || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer</span>
                    <span className="font-medium">{viewHistoryTransaction.customer_name || 'Walk-in'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment</span>
                    <span className="font-medium capitalize">{viewHistoryTransaction.payment_method}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cashier</span>
                    <span className="font-medium">{viewHistoryTransaction.cashier_name || viewHistoryTransaction.user_name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Voucher</span>
                    <span className="font-mono text-xs">{viewHistoryTransaction.voucher_number || '-'}</span>
                  </div>
                  {viewHistoryTransaction.payment_adjustment && Math.abs(viewHistoryTransaction.payment_adjustment) > 0.001 && (
                    <div className="flex justify-between col-span-2">
                      <span className="text-muted-foreground">Adjustment</span>
                      <span className={`font-mono ${viewHistoryTransaction.payment_adjustment > 0 ? 'text-orange-400' : 'text-purple-400'}`}>
                        {viewHistoryTransaction.payment_adjustment > 0 ? '+' : ''}{formatUSD(viewHistoryTransaction.payment_adjustment)} 
                        ({viewHistoryTransaction.payment_adjustment > 0 ? 'Discount' : 'Premium'})
                      </span>
                    </div>
                  )}
                </div>

                {/* Items Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 font-medium">Item</th>
                        <th className="text-center p-2 font-medium w-12">Qty</th>
                        <th className="text-right p-2 font-medium w-20">Price</th>
                        <th className="text-center p-2 font-medium w-14">Disc%</th>
                        <th className="text-right p-2 font-medium w-20">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(viewHistoryTransaction.lines || []).map((line, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="p-2 text-xs">{line.item_name || '-'}</td>
                          <td className={`p-2 text-center ${(line.quantity || 0) < 0 ? 'text-red-400' : ''}`}>{line.quantity || 0}</td>
                          <td className="p-2 text-right font-mono text-xs">{formatUSD(line.unit_price || 0)}</td>
                          <td className="p-2 text-center text-xs">{line.discount_percent || 0}%</td>
                          <td className={`p-2 text-right font-mono text-xs ${(line.line_total_usd || line.line_total || 0) < 0 ? 'text-red-400' : ''}`}>
                            {formatUSD(line.line_total_usd || line.line_total || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t pt-3 space-y-1">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Subtotal</span>
                    <span className="font-mono">${formatUSD(viewHistoryTransaction.subtotal_usd || 0)}</span>
                  </div>
                  {(viewHistoryTransaction.tax_amount || 0) !== 0 && (
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Tax ({viewHistoryTransaction.tax_percent || 0}%)</span>
                      <span className="font-mono">${formatUSD(viewHistoryTransaction.tax_amount || 0)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-semibold pt-1 border-t">
                    <span>Total (XOF)</span>
                    <span className={`font-mono ${(viewHistoryTransaction.total_usd || 0) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                      ${formatUSD(viewHistoryTransaction.total_usd || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total (EUR)</span>
                    <span className="font-mono text-cyan-400">
                      € {Math.round(viewHistoryTransaction.total_lbp || (viewHistoryTransaction.total_usd || 0) * (viewHistoryTransaction.lbp_rate || exchangeRate || 0.0015244895)).toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Void Info if voided */}
                {viewHistoryTransaction.is_voided && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
                    <div className="font-medium text-red-400 mb-1">Voided Transaction</div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Reason: {viewHistoryTransaction.void_reason || 'N/A'}</div>
                      <div>By: {viewHistoryTransaction.voided_by_name || 'Admin'}</div>
                      <div>At: {viewHistoryTransaction.voided_at || '-'}</div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-3 gap-2">
                {!viewHistoryTransaction.is_voided && (
                  <Button variant="outline" size="sm" onClick={() => printThermalReceipt(viewHistoryTransaction)}>
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => setViewHistoryTransaction(null)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Permanently Delete
            </DialogTitle>
            <DialogDescription>
              This will permanently remove this transaction from the system. This cannot be undone. Use "Void" instead to keep an audit trail.
            </DialogDescription>
          </DialogHeader>
          
          {deleteConfirm && (
            <div className="space-y-3 py-2">
              <div className="bg-muted/30 p-3 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Receipt:</span>
                  <span className="font-mono font-medium">{deleteConfirm.receipt_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{formatDate(deleteConfirm.date)} {deleteConfirm.time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-mono font-bold text-green-400">${formatUSD(deleteConfirm.total_usd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Voucher:</span>
                  <span className="font-mono">{deleteConfirm.voucher_number}</span>
                </div>
              </div>
              
              <div className="text-sm text-amber-400 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>This will permanently delete the transaction, voucher, and restore inventory.</span>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={processing}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteTransaction(deleteConfirm)}
              disabled={processing}
              data-testid="confirm-delete-btn"
            >
              {processing ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Transaction Dialog */}
      <Dialog open={!!voidConfirm} onOpenChange={() => { setVoidConfirm(null); setVoidReason(''); }}>
        <DialogContent className="max-w-md" data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <X className="w-5 h-5" />
              Void Transaction
            </DialogTitle>
            <DialogDescription>
              Void this transaction to reverse its accounting entries and restore inventory. The transaction will remain visible in history for audit purposes.
            </DialogDescription>
          </DialogHeader>
          
          {voidConfirm && (
            <div className="space-y-3 py-2">
              <div className="bg-muted/30 p-3 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Receipt:</span>
                  <span className="font-mono font-medium">{voidConfirm.receipt_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total:</span>
                  <span className="font-mono font-bold text-green-400">${formatUSD(voidConfirm.total_usd)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Voucher:</span>
                  <span className="font-mono">{voidConfirm.voucher_number}</span>
                </div>
              </div>
              
              <div className="space-y-1.5">
                <Label className="text-xs">Reason for Voiding *</Label>
                <Input
                  value={voidReason}
                  onChange={e => setVoidReason(e.target.value)}
                  placeholder="e.g., Customer returned items, wrong order, duplicate entry..."
                  data-testid="void-reason-input"
                  autoFocus
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setVoidConfirm(null); setVoidReason(''); }} disabled={processing}>
              Cancel
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleVoidTransaction}
              disabled={processing || !voidReason.trim()}
              data-testid="confirm-void-btn"
            >
              {processing ? 'Voiding...' : 'Void Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer History Dialog */}
      <Dialog open={showCustomerHistory} onOpenChange={setShowCustomerHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Customer Purchase History
              {selectedCustomerId && (
                <span className="text-muted-foreground font-normal">
                  - {customerAccounts.find(c => c.id === selectedCustomerId)?.name}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh]">
            {customerHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No purchase history found for this customer
              </div>
            ) : (
              <div className="space-y-3">
                {customerHistory.map(t => (
                  <Card key={t.id} className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-mono text-sm">{t.receipt_number}</span>
                        <span className="text-xs text-muted-foreground ml-2">{formatDate(t.date)} {t.time}</span>
                      </div>
                      <span className="font-mono font-bold text-green-400">${formatUSD(t.total_usd)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t.lines.map(l => l.item_name).join(', ')}
                    </div>
                    <div className="flex items-center justify-between mt-2 text-xs">
                      <span className="capitalize">{t.payment_method}</span>
                      <Button variant="ghost" size="sm" className="h-6" onClick={() => printThermalReceipt(t)}>
                        <Printer className="w-3 h-3 mr-1" />
                        Reprint
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Settings Dialog */}
      <ReceiptSettingsDialog
        open={showReceiptSettings}
        onOpenChange={(open) => {
          setShowReceiptSettings(open);
          if (!open && currentOrg) {
            axios.get(`${API}/receipt-settings?organization_id=${currentOrg.id}`)
              .then(res => setReceiptSettings(res.data))
              .catch(() => {});
          }
        }}
        organizationId={currentOrg?.id}
      />

      {/* Quick Add Customer Dialog */}
      <QuickAddAccountDialog open={qaCustomerOpen} onOpenChange={setQaCustomerOpen}
        type="customer" mode={qaCustomerMode} editData={qaCustomerEdit}
        organizationId={currentOrg?.id} onSaved={(acc) => {
          setCustomerAccounts(prev => {
            const exists = prev.find(c => c.id === acc.id);
            if (exists) return prev.map(c => c.id === acc.id ? acc : c);
            return [...prev, acc];
          });
          if (qaCustomerMode === 'add') setSelectedCustomerId(acc.id);
        }} />

      {/* Quick Add/Edit Inventory Item Dialog */}
      <QuickAddInventoryDialog open={qaItemOpen} onOpenChange={setQaItemOpen}
        mode={qaItemMode} editData={qaItemEdit}
        organizationId={currentOrg?.id} onSaved={(item) => {
          if (qaItemMode === 'add') {
            setInventoryItems(prev => [...prev, item]);
            addToCart(item);
          } else {
            setInventoryItems(prev => prev.map(i => i.id === item.id ? item : i));
          }
        }} />

      {/* Drug Interaction Checker */}
      <DrugInteractionDialog
        open={showInteractions}
        onOpenChange={setShowInteractions}
        cartItems={cart}
      />

      {/* Camera Barcode Scanner */}
      <BarcodeScannerDialog
        open={showCameraScanner}
        onClose={() => setShowCameraScanner(false)}
        onScan={handleCameraScan}
      />

      {/* Return Batch Selector Dialog */}
      <Dialog open={!!returnBatchSelector} onOpenChange={(open) => !open && setReturnBatchSelector(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-orange-500" />
              Select Batch for Return
            </DialogTitle>
            <DialogDescription>
              Choose which batch to return the item to, or enter new batch/expiry
            </DialogDescription>
          </DialogHeader>
          
          {returnBatchSelector && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium">{returnBatchSelector.item?.item_name}</div>
                <div className="text-sm text-muted-foreground">
                  Returning: {Math.abs(returnBatchSelector.item?.quantity || 0)} unit(s)
                </div>
              </div>

              {/* Existing batches */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Existing Batches</Label>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {itemBatches[returnBatchSelector.item?.inventory_item_id]?.length > 0 ? (
                    itemBatches[returnBatchSelector.item?.inventory_item_id].map(batch => (
                      <button
                        key={batch.id}
                        onClick={() => updateReturnBatch(
                          returnBatchSelector.index,
                          batch.id,
                          batch.batch_number,
                          batch.expiry_date
                        )}
                        className="w-full text-left p-2 rounded border hover:bg-orange-50 hover:border-orange-300 transition-colors"
                      >
                        <div className="flex justify-between">
                          <span className="font-mono text-sm">{batch.batch_number}</span>
                          <span className="text-xs text-muted-foreground">Qty: {batch.quantity}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Expiry: {formatExpiry(batch.expiry_date)}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-2">
                      No existing batches found
                    </div>
                  )}
                </div>
              </div>

              {/* Or enter new batch */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Or Create New Batch</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-xs">Batch Number</Label>
                    <Input
                      id="new-batch-number"
                      placeholder="e.g., RET-001"
                      className="h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Expiry Date</Label>
                    <Input
                      id="new-batch-expiry"
                      type="date"
                      className="h-9"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => {
                    const batchNum = document.getElementById('new-batch-number')?.value;
                    const expiry = document.getElementById('new-batch-expiry')?.value;
                    if (batchNum) {
                      updateReturnBatch(
                        returnBatchSelector.index,
                        `new_${Date.now()}`,
                        batchNum,
                        expiry || null
                      );
                    } else {
                      toast.error('Please enter a batch number');
                    }
                  }}
                >
                  Use New Batch
                </Button>
              </div>

              {/* Skip batch selection */}
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  updateReturnBatch(returnBatchSelector.index, 'no_batch', 'General Stock', null);
                }}
              >
                Skip (Add to General Stock)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cashier Today's Sales History Dialog */}
      <Dialog open={showCashierHistory} onOpenChange={setShowCashierHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              Today's Sales
            </DialogTitle>
            <DialogDescription>
              {todayTransactions.length} transactions today
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[60vh]">
            {todayTransactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No transactions today
              </div>
            ) : (
              <div className="space-y-2">
                {todayTransactions.map(t => (
                  <div 
                    key={t.id} 
                    className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => setViewCashierTransaction(t)}
                    data-testid={`cashier-txn-${t.receipt_number}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono text-sm font-medium">{t.receipt_number}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.time} • {t.customer_name || 'Walk-in'} • {t.payment_method}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">{t.lines?.length || 0} items</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cashier Transaction Detail View Dialog */}
      <Dialog open={!!viewCashierTransaction} onOpenChange={(open) => !open && setViewCashierTransaction(null)}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          {viewCashierTransaction && (
            <>
              <DialogHeader className="border-b pb-3">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <span className="font-mono">{viewCashierTransaction.receipt_number}</span>
                </DialogTitle>
                <DialogDescription className="text-xs">Transaction Details</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-2">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span className="font-medium">{formatDate(viewCashierTransaction.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    <span className="font-medium">{viewCashierTransaction.time || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Customer</span>
                    <span className="font-medium">{viewCashierTransaction.customer_name || 'Walk-in'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment</span>
                    <span className="font-medium capitalize">{viewCashierTransaction.payment_method}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cashier</span>
                    <span className="font-medium">{viewCashierTransaction.user_name || user?.name || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Voucher</span>
                    <span className="font-mono text-xs">{viewCashierTransaction.voucher_number || '-'}</span>
                  </div>
                </div>

                {/* Items Table */}
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-2 font-medium">Item</th>
                        <th className="text-center p-2 font-medium w-12">Qty</th>
                        <th className="text-right p-2 font-medium w-20">Price</th>
                        <th className="text-center p-2 font-medium w-14">Disc%</th>
                        <th className="text-right p-2 font-medium w-20">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {viewCashierTransaction.lines?.map((line, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="p-2 text-xs">{line.item_name}</td>
                          <td className="p-2 text-center">{line.quantity}</td>
                          <td className="p-2 text-right font-mono text-xs">{formatUSD(line.unit_price)}</td>
                          <td className="p-2 text-center text-xs">{line.discount_percent || 0}%</td>
                          <td className="p-2 text-right font-mono text-xs">{formatUSD(line.line_total_usd || line.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals */}
                <div className="border-t pt-3 space-y-1">
                  <div className="flex justify-between text-base font-semibold">
                    <span>Total (XOF)</span>
                    <span className="font-mono text-green-500">${formatUSD(viewCashierTransaction.total_usd)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total (EUR)</span>
                    <span className="font-mono text-cyan-400">€ {Math.round(viewCashierTransaction.total_lbp || viewCashierTransaction.total_usd * (viewCashierTransaction.lbp_rate || exchangeRate)).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setViewCashierTransaction(null)}>
                  Close
                </Button>
                <Button onClick={() => printThermalReceipt(viewCashierTransaction)}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print Receipt
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Parked Transactions Dialog */}
      <Dialog open={showParkedList} onOpenChange={setShowParkedList}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Parked Transactions
            </DialogTitle>
            <DialogDescription>
              {parkedTransactions.length === 0 
                ? 'No parked transactions. Park a cart to serve another customer.' 
                : 'Select a transaction to resume.'}
            </DialogDescription>
          </DialogHeader>
          
          {parkedTransactions.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {parkedTransactions.map((parked) => (
                <div 
                  key={parked.id}
                  className="p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-amber-500">{parked.time}</span>
                      <span className="text-xs text-muted-foreground">{parked.date}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-500"
                      onClick={() => handleDeleteParked(parked.id)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span className="text-sm">{parked.customerName}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {parked.cart.length} item{parked.cart.length !== 1 ? 's' : ''}
                    </span>
                    <span className="font-medium text-emerald-500">
                      ${formatUSD(parked.totals?.totalUsd || 0)}
                    </span>
                  </div>
                  {parked.notes && (
                    <div className="text-xs text-muted-foreground mt-1 italic">
                      Note: {parked.notes}
                    </div>
                  )}
                  <Button 
                    className="w-full mt-2" 
                    size="sm"
                    onClick={() => handleResumeTransaction(parked)}
                  >
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    Resume
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No parked transactions</p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParkedList(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Controlled Substance Dialog */}
      <Dialog open={showControlledDialog} onOpenChange={setShowControlledDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-500">
              <AlertTriangle className="w-5 h-5" />
              Controlled Substance - Prescription Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <p className="text-sm font-medium">{pendingControlledItem?.name}</p>
              <p className="text-xs text-muted-foreground">Select patient to lookup existing prescription</p>
            </div>
            
            {/* Step 1: Select Patient - Enhanced with Search */}
            <div className="space-y-2">
              <Label>1. Select Patient *</Label>
              
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, phone, or physician..."
                  value={patientSearchQuery}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  className="pl-9"
                />
                {patientSearchLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              
              {/* Patient List */}
              <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                {/* Patients WITH prescription for this item */}
                {patientsWithPrescription.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-green-500/10 text-xs font-medium text-green-700 dark:text-green-400 sticky top-0 border-b flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Patients with Prescription for this Item ({patientsWithPrescription.length})
                    </div>
                    {patientsWithPrescription.map(p => (
                      <div
                        key={p.id}
                        onClick={() => handlePatientSelect(p.id)}
                        className={`px-3 py-2 cursor-pointer hover:bg-green-500/10 border-b last:border-b-0 flex items-center justify-between ${
                          controlledFormData.patient_id === p.id ? 'bg-green-500/20 border-l-2 border-l-green-500' : ''
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium">{p.name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {p.name_ar && <span className="text-right" dir="rtl">{p.name_ar}</span>}
                            {p.phone && <span>📞 {p.phone}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          {p.prescription_physician && (
                            <p className="text-xs text-green-600 dark:text-green-400">Dr. {p.prescription_physician}</p>
                          )}
                          {p.last_prescription_date && (
                            <p className="text-xs text-muted-foreground">{p.last_prescription_date}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Patients WITHOUT prescription */}
                {patientsWithoutPrescription.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 bg-muted/50 text-xs font-medium text-muted-foreground sticky top-0 border-b">
                      All Other Patients ({patientsWithoutPrescription.length})
                    </div>
                    {patientsWithoutPrescription.map(p => (
                      <div
                        key={p.id}
                        onClick={() => handlePatientSelect(p.id)}
                        className={`px-3 py-2 cursor-pointer hover:bg-muted/50 border-b last:border-b-0 ${
                          controlledFormData.patient_id === p.id ? 'bg-primary/10 border-l-2 border-l-primary' : ''
                        }`}
                      >
                        <p className="text-sm">{p.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {p.name_ar && <span className="text-right" dir="rtl">{p.name_ar}</span>}
                          {p.phone && <span>📞 {p.phone}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Empty state */}
                {patientsWithPrescription.length === 0 && patientsWithoutPrescription.length === 0 && !patientSearchLoading && (
                  <div className="px-3 py-6 text-center text-muted-foreground">
                    <User className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No patients found</p>
                    <p className="text-xs mt-1">Add patients in Prescription Register → Patients</p>
                  </div>
                )}
                
                {/* Loading state */}
                {patientSearchLoading && patientsWithPrescription.length === 0 && patientsWithoutPrescription.length === 0 && (
                  <div className="px-3 py-6 text-center text-muted-foreground">
                    <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                    <p className="text-sm">Loading patients...</p>
                  </div>
                )}
              </div>
              
              {/* Selected patient indicator */}
              {controlledFormData.patient_id && (
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>Selected: {patients.find(p => p.id === controlledFormData.patient_id)?.name}</span>
                </div>
              )}
            </div>

            {/* Loading indicator */}
            {prescriptionLookupLoading && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                <p className="text-sm text-blue-600">Looking up prescriptions...</p>
              </div>
            )}

            {/* Step 2: Show prescription result */}
            {controlledFormData.patient_id && !prescriptionLookupLoading && (
              <>
                {selectedPrescription ? (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-sm font-medium text-green-700 dark:text-green-400">
                        Valid Prescription Found
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Serial #:</span>
                        <span className="ml-1 font-medium">{selectedPrescription?.serial_number || '-'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Date:</span>
                        <span className="ml-1">{selectedPrescription?.date || '-'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Physician:</span>
                        <span className="ml-1 font-medium">{selectedPrescription?.physician_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Remaining:</span>
                        <span className="ml-1">{selectedPrescription?.remaining_pickups ?? 1} pickup(s)</span>
                      </div>
                    </div>
                    
                    {selectedPrescription?.instructions && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Instructions:</span>
                        <p className="mt-1 text-xs bg-white/50 dark:bg-black/20 p-2 rounded" dir="auto">
                          {selectedPrescription.instructions}
                        </p>
                      </div>
                    )}
                    
                    {/* Dosage info */}
                    {(selectedPrescription?.dosage_gram || selectedPrescription?.dosage_cent || 
                      selectedPrescription?.dosage_mill || selectedPrescription?.dosage_gtes) && (
                      <div className="text-sm">
                        <span className="text-muted-foreground">Dosage:</span>
                        <div className="flex gap-2 mt-1 text-xs">
                          {selectedPrescription?.dosage_gram > 0 && <Badge variant="outline">{selectedPrescription.dosage_gram}g</Badge>}
                          {selectedPrescription?.dosage_cent > 0 && <Badge variant="outline">{selectedPrescription.dosage_cent}cg</Badge>}
                          {selectedPrescription?.dosage_mill > 0 && <Badge variant="outline">{selectedPrescription.dosage_mill}mg</Badge>}
                          {selectedPrescription?.dosage_gtes > 0 && <Badge variant="outline">{selectedPrescription.dosage_gtes} drops</Badge>}
                        </div>
                      </div>
                    )}
                    
                    {/* Prescription Image */}
                    {selectedPrescription?.prescription_image_url && (
                      <div className="space-y-1">
                        <span className="text-sm text-muted-foreground">Prescription Image:</span>
                        <div 
                          className="border rounded-lg overflow-hidden cursor-pointer hover:opacity-90"
                          onClick={() => setShowPrescriptionImage(selectedPrescription.prescription_image_url)}
                        >
                          <img 
                            src={selectedPrescription.prescription_image_url} 
                            alt="Prescription" 
                            className="w-full max-h-[150px] object-contain bg-gray-100 dark:bg-gray-900"
                          />
                        </div>
                        <p className="text-[10px] text-muted-foreground text-center">Click to view full size</p>
                      </div>
                    )}
                    
                    {/* Next pickup info */}
                    {selectedPrescription?.next_pickup && (
                      <div className="text-xs p-2 bg-blue-500/10 rounded">
                        <span className="text-muted-foreground">Next pickup window:</span>
                        <span className="ml-1 font-medium">
                          {selectedPrescription.next_pickup?.pickup_window_start || '-'} to {selectedPrescription.next_pickup?.pickup_window_end || '-'}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center space-y-2">
                    <div className="flex items-center justify-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <p className="text-sm font-medium text-red-700 dark:text-red-400">
                        No Valid Prescription Found
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      This patient does not have a valid prescription for {pendingControlledItem?.name}.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Please register a new prescription first in the Prescription Register module.
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowControlledDialog(false);
                        window.open('/prescription-register', '_blank');
                      }}
                    >
                      Go to Prescription Register
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowControlledDialog(false); setPendingControlledItem(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleControlledSubmit} 
              className="bg-orange-500 hover:bg-orange-600"
              disabled={!selectedPrescription}
            >
              Add to Cart
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Image Viewer Modal */}
      <Dialog open={!!showPrescriptionImage} onOpenChange={() => setShowPrescriptionImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Prescription Image</DialogTitle>
          </DialogHeader>
          {showPrescriptionImage && (
            <div className="flex justify-center">
              <img 
                src={showPrescriptionImage} 
                alt="Prescription" 
                className="max-w-full max-h-[70vh] object-contain"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrescriptionImage(null)}>Close</Button>
            <Button onClick={() => window.open(showPrescriptionImage, '_blank')}>Open in New Tab</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Early Pickup Warning Dialog */}
      <Dialog open={!!earlyPickupWarning} onOpenChange={(open) => !open && setEarlyPickupWarning(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Early Pickup Warning
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm font-medium">
                {earlyPickupWarning?.patient_name} has an active prescription for {earlyPickupWarning?.medication_name}
              </p>
              <p className="text-sm mt-2">
                Next pickup window starts: <strong>{earlyPickupWarning?.next_window_start}</strong>
              </p>
            </div>
            
            {earlyPickupWarning?.prescriptions?.length > 0 && (
              <div className="text-xs space-y-1">
                <p className="font-medium">Active prescriptions:</p>
                {earlyPickupWarning.prescriptions.slice(0, 3).map((p, i) => (
                  <div key={i} className="flex justify-between text-muted-foreground">
                    <span>#{p.serial_number} - {p.medication_name}</span>
                    <span>{p.pickup_window_start} to {p.pickup_window_end}</span>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-sm text-muted-foreground">
              Proceeding may indicate early pickup behavior. Continue anyway?
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCancelEarlyPickup}>
              Cancel
            </Button>
            <Button onClick={handleProceedWithEarlyPickup} variant="destructive">
              Proceed Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription Label Dialog */}
      <PrescriptionLabelDialog 
        open={showPrescriptionLabel} 
        onOpenChange={setShowPrescriptionLabel} 
      />
    </div>
  );
};

export default POSPage;
